/**
 * The config registry: the one source of every config key the game loads (`30_seed_data.md`
 * SEED-150, SEED-T09: "the registry equals defaults.ts"; `26_admin_tools.md` ADM-020: the ⚖
 * marks come from this file). For each key: its default, how it is stored, its range where the
 * spec gives one, whether it carries the fairness mark ⚖, whether the apps may read it, and the
 * file that owns it.
 *
 * - The keys are those `02_decisions_log.md` §4 (§4.1 to §4.16) prints with a value, and the
 *   few that only their owner's key table prints and that a built stage reads (`section:
 *   'owner'`: `04` §8.1's four auth keys, `05` §4's data and jobs keys, `26` §4's admin keys and
 *   `20` §4's break reminder). Other owners' keys join in the stage that builds their owner
 *   (SEED-150, "keys that arrive with a later stage").
 * - Each key is its owner's: the owner file's §4 holds the full row and wins (`02` §4.15,
 *   §4.16). A value is held in the unit the spec prints for it (`10` SIM-006); `unit` names it.
 * - Keys that §4 withdraws or retires are not here (SEED-150): `live.decision_frequency`,
 *   `legacy.inheritance_tax_rate`, `legacy.inheritance_tax_free_coins`, the three retired
 *   `clothing.*` keys, `practice.knowledge_bonus_pct`, `practice.knowledge_window_days`,
 *   `ai_pop.juniors`, the names of §4.16's last row, and `26`'s
 *   `admin.takedown_propagation_seconds`, which has no value of its own (`26` §4: "There is one
 *   takedown time, not two").
 * - Two names that §4 prints are not keys, because §4 gives them no value: `rounds.daylight_rule`
 *   (a rule, `06` §3.6, whose numbers are the `daylight.*` keys of `06` §4) and `caddie.level`
 *   (each caddie's own level, 1 to 5, with the names of D-19).
 * - The database is generated from this file: `supabase/seed/generate-config-seed.mjs` writes
 *   `supabase/seed/config_defaults.json`, the seed SQL and the pgTAP test from it, and CI fails
 *   if they are out of date (`test/config.defaults.test.ts`, `test/config.seed.test.ts`).
 *
 * This file must stay plain TypeScript that Node can run with its type stripping (no enums,
 * no namespaces, type-only imports), because the database job loads it without a build step.
 */
import type {
  ConfigKeySpec,
  ConfigUnit,
  ConfigValue,
  ConfigValueType,
  KeySection,
  SpecFile,
} from './types';

interface Extra {
  /** One plain sentence for the admin console. */
  readonly desc: string;
  /** Where the value was read. */
  readonly src: string;
  readonly fair?: boolean;
  readonly range?: readonly [number, number];
  readonly options?: readonly string[];
  readonly fixed?: boolean;
  /** The apps may read it. */
  readonly client?: boolean;
  /** How it is stored, where the default does not show it. */
  readonly type?: ConfigValueType;
  readonly note?: string;
  /** The build stage that brought the key, where it is not Stage 0 (SEED-150, version 2). */
  readonly stage?: number;
}

/** How a default is stored when the key names no type (`05` DAT-050). */
export function configValueType(value: ConfigValue): ConfigValueType {
  if (typeof value === 'boolean') return 'bool';
  if (typeof value === 'string') return 'text';
  if (typeof value === 'number') return Number.isInteger(value) ? 'int' : 'numeric';
  if (Array.isArray(value) && value.length > 0 && value.every((x) => Number.isInteger(x)))
    return 'int_list';
  return 'json';
}

function k(
  section: KeySection,
  owner: SpecFile,
  key: string,
  value: ConfigValue,
  unit: ConfigUnit,
  extra: Extra,
): ConfigKeySpec {
  if (value === null && extra.type === undefined)
    throw new Error('config: ' + key + ' has no default, so it must name its type');
  return {
    key,
    default: value,
    valueType: extra.type ?? configValueType(value),
    nullable: value === null,
    fair: extra.fair === true,
    client: extra.client === true,
    owner,
    section,
    unit,
    description: extra.desc,
    source: extra.src,
    ...(extra.range !== undefined ? { range: { min: extra.range[0], max: extra.range[1] } } : {}),
    ...(extra.options !== undefined ? { options: extra.options } : {}),
    ...(extra.fixed === true ? { fixed: true } : {}),
    ...(extra.note !== undefined ? { note: extra.note } : {}),
    ...(extra.stage !== undefined && extra.stage > 0 ? { stage: extra.stage } : {}),
  };
}

/** A key that only its owner's key table prints (not `02` §4). */
function own(
  owner: SpecFile,
  key: string,
  value: ConfigValue,
  unit: ConfigUnit,
  extra: Extra,
): ConfigKeySpec {
  return k('owner', owner, key, value, unit, extra);
}

const F = { fair: true } as const;

/** Keys that arrive with Stage 2: their version 1 is approved in the batch `stage2_initial`. */
const S2 = { stage: 2 } as const;

/** Keys that arrive with Stage 3 (the AI gateway, names and biographies): batch `stage3_initial`. */
const S3 = { stage: 3 } as const;

/** Keys that arrive with Stage 4 (the training code, the ledger's prices, the school calendar): batch `stage4_initial`. */
const S4 = { stage: 4 } as const;

/** Keys that arrive with Stage 5 (the season clock and the calendar of `06`): batch `stage5_initial`. */
const S5 = { stage: 5 } as const;

/** The days of the week, as an option key holds them (`clubs.range_night_day`, 12 §4). */
const WEEKDAYS = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
] as const;

export const CONFIG_DEFAULTS: readonly ConfigKeySpec[] = [
  // ---------------------------------------------------------------- 4.1 Time
  k('4.1', '06', 'time.season_length_days', 91, 'days', {
    client: true,
    desc: 'Length of a season in days: 13 weeks (12 playing weeks and the Break Week). FIXED after launch.',
    src: '02 §4.1; c03, R-44',
    fixed: true,
    range: [91, 91],
    note: '13 weeks: 12 playing weeks and the Break Week. FIXED after launch',
  }),
  k(
    '4.1',
    '06',
    'time.season_start',
    { weekday: 'monday', local_time: '09:00', time_zone: 'Europe/London' },
    'structure',
    {
      client: true,
      desc: 'When a season starts: Monday 09:00 Europe/London; seasons follow each other with no gap.',
      src: '02 §4.1',
      note: 'Seasons follow each other with no gap',
    },
  ),
  k('4.1', '06', 'time.season1_start_at', null, 'timestamp', {
    client: true,
    desc: 'Launch day: the instant main Season 1 starts (an allowed Monday 09:00 Europe/London, ISO 8601). Unset until the founder agrees the date.',
    src: "02 §4.1; 06 §4; 06 CLK-003 (no default: unset until agreed; any other Monday needs the founder's approval through the approval queue ⚖)",
    ...F,
    type: 'text',
    note: 'Launch day, 09:00 Europe/London on an allowed Monday (06 CLK-003; 32 LCH-041). The founder agrees the date',
  }),
  k('4.1', '06', 'time.daily_reset_local', '04:00', 'time_of_day', {
    client: true,
    desc: "Daily reset, local time in the golfer's home-club time zone.",
    src: '02 §4.1',
    note: "In the golfer's home-club time zone",
  }),
  k('4.1', '06', 'ageing.age_years_per_season', { under_18: 2.0, adult: 1.0 }, 'years_per_season', {
    desc: 'Years of age a golfer gains each season: under 18 two birthdays, 18 and over one.',
    src: '02 §4.1; c04',
  }),
  k(
    '4.1',
    '08',
    'ageing.start_age_bands',
    {
      junior: [8, 15],
      beginner: [8, 70],
      senior: [50, 70],
      real_handicap: [13, 80],
      elite_amateur: [16, 30],
      tour_pro: [18, 35],
      random_life: [8, 45],
    },
    'years',
    {
      client: true,
      desc: 'Allowed starting ages for each start path, [youngest, oldest].',
      src: '02 §4.1; h03-09',
    },
  ),
  k('4.1', '06', 'calendar.major_default_start_days', [18, 39, 60, 81], 'days', {
    desc: 'Season days on which the four majors start (Thursday of weeks 3, 6, 9 and 12).',
    src: '02 §4.1 (season template; days of the 91-day season)',
    range: [1, 91],
    note: 'Days of the 91-day season',
  }),

  // ---------------------------------------------------------------- 4.2 Golf hours, energy and training
  k(
    '4.2',
    '09',
    'golfer.golf_hours_per_day',
    {
      adult: 6,
      tour_pro: 8,
      junior_school_day: 3,
      junior_school_day_golf_focus: 4,
      junior_other_day: 6,
    },
    'hours',
    {
      client: true,
      desc: 'Golf hours a day: adults 6, tour professionals 8, juniors 3 on term-time school days (4 with Golf focus) and 6 otherwise.',
      src: '02 §4.2; h04-07, h06-26, R-17',
    },
  ),
  k('4.2', '09', 'training.extra_hours_max_per_day', 4, 'hours', {
    client: true,
    desc: 'Most bought extra golf hours a day, inside the one cap.',
    src: '02 §4.2; c27',
    ...F,
    range: [0, 4],
    note: 'Juniors only at weekends and in school holidays. Inside the one cap',
  }),
  k('4.2', '08', 'energy.max', 100, 'points', {
    client: true,
    desc: 'Full energy. Refills with real time only; no paid refills anywhere.',
    src: '02 §4.2; h17-18, h11-13',
    ...F,
    note: 'Refills with real time only, fully in about 10 hours of rest; no paid refills',
  }),
  k('4.2', '09', 'training.away_rate', 0.7, 'share', {
    desc: "Share of a plan's effect earned on a game day the player has not checked in.",
    src: '02 §4.2; h04-10',
  }),
  k('4.2', '09', 'training.rested_bonus_max_days', 3, 'days', {
    desc: 'Days of stored rested bonus at most.',
    src: '02 §4.2; h11-21',
  }),
  k('4.2', '09', 'training.daily_practice_soft_cap_hours', 4, 'hours', {
    desc: 'Practice hours a day after which training is at 50% efficiency.',
    src: '02 §4.2; h11-21',
    note: 'Hours beyond this train at 50% efficiency',
  }),
  k('4.2', '09', 'training.home_club_max_bonus', 0.25, 'share', {
    desc: 'Most bonus from home-club quality and facilities combined.',
    src: '02 §4.2; h04-11',
  }),
  k('4.2', '09', 'training.total_multiplier_cap', 2.0, 'multiplier', {
    ...F,
    type: 'numeric',
    range: [1.5, 2.5],
    desc: 'Most multiple of the base training rate from all boosts combined.',
    src: "02 §4.2; range from 26 ADM-021's Accept (09 §4 prints the range as FIXED: an open point)",
  }),
  k('4.2', '09', 'training.paid_origin_cap', 0.56, 'share', {
    desc: "Most that anything funded with real money adds on its own (+56%). Proved by the training package's seeded run (TRN-170, TRN-171).",
    src: '02 §4.2; c27; ruling K1',
    ...F,
    note: 'The one cap; 09 owns the value and its range (TRN-170, TRN-171)',
  }),
  k('4.2', '09', 'fairplay.max_time_saving', 0.4, 'share', {
    client: true,
    desc: "The one cap: the biggest spender reaches any level in no less than 0.60 of the committed free player's time.",
    src: '02 §4.2; c27',
    ...F,
    range: [0.2, 0.4],
  }),
  k('4.2', '09', 'potential.limit_lift_max', 2.0, 'points', {
    type: 'numeric',
    desc: "Most points a skill's limit can be lifted by every bought route together.",
    src: '02 §4.2; r02-03',
    ...F,
    range: [0, 2.0],
  }),
  k('4.2', '09', 'potential.limit_lift_days', 126, 'days', {
    desc: 'Days of top support to earn the full limit lift.',
    src: '02 §4.2; r02-03',
  }),
  k(
    '4.2',
    '08',
    'rust.extra_shots',
    { declared_up_to_10_0: 3.0, declared_10_1_to_20_0: 4.0, declared_above_20_0: 5.0 },
    'shots',
    {
      desc: 'Extra shots added for rust, by declared handicap band.',
      src: '02 §4.2; 08 GOL-044; R-12',
    },
  ),
  k('4.2', '08', 'rust.half_life_active_days', 4, 'days', {
    desc: 'Active game days for rust to halve.',
    src: '02 §4.2; 08 GOL-044; R-12',
  }),
  k('4.2', '08', 'start.cap_unverified', 5.0, 'strokes', {
    type: 'numeric',
    desc: 'Best starting handicap for an unverified real-handicap start.',
    src: '02 §4.2; h03-08',
  }),
  k('4.2', '08', 'start.cap_verified', 0.0, 'strokes', {
    type: 'numeric',
    desc: 'Best starting handicap for a verified real-handicap start (scratch).',
    src: '02 §4.2; h03-08',
    note: 'Scratch',
  }),

  // ---------------------------------------------------------------- 4.4 Rounds and events
  k('4.4', '14', 'live.round_target_minutes', 20, 'minutes', {
    client: true,
    desc: 'Target length of an 18-hole round in Every shot with one-tap play.',
    src: '02 §4.4; c08, R-47',
    note: '18 holes in the Every shot style with one-tap play',
  }),
  k('4.4', '14', 'live.decision_window_seconds', 60, 'seconds', {
    client: true,
    desc: 'Longest any single decision may take.',
    src: '02 §4.4; R-01, R-47',
  }),
  k('4.4', '14', 'round.play_style', 'key_moments', 'option', {
    client: true,
    desc: 'Default way to play a round: Caddie plays it, Key moments or Every shot. Each player chooses for each round.',
    src: '02 §4.4; 05 §4; 14 CMP-120; R-48',
    options: ['caddie', 'key_moments', 'every_shot'],
    note: "The player chooses for each round; the default of the setting is 14 CMP-120's",
  }),
  k('4.4', '14', 'round.time_bank_seconds', 900, 'seconds', {
    client: true,
    desc: 'Time bank for 18 holes in Every shot (50 seconds a hole).',
    src: '02 §4.4; c08, R-47',
    note: 'every_shot, 18 holes',
  }),
  k('4.4', '14', 'key_moments.stop_seconds', 45, 'seconds', {
    client: true,
    desc: 'Seconds at each stop in Key moments.',
    src: '02 §4.4',
  }),
  k('4.4', '14', 'match.shot_clock_seconds', 40, 'seconds', {
    client: true,
    desc: 'Shot clock for each shot in live team match play.',
    src: '02 §4.4',
  }),
  k('4.4', '10', 'round.focus_max', 100, 'points', {
    type: 'numeric',
    client: true,
    desc: 'Full focus (held to one decimal place, shown as a whole number).',
    src: '02 §4.4; ruling L12',
  }),
  k('4.4', '14', 'instant.replay_seconds', 30, 'seconds', {
    client: true,
    desc: 'Length of an instant replay (skippable).',
    src: '02 §4.4; h10-05',
  }),

  // ---------------------------------------------------------------- 4.5 Economy and money
  k('4.5', '18', 'aces.base_rate', { aces: 100, gbp: 0.99, usd: 0.99, eur: 0.99 }, 'structure', {
    ...F,
    client: true,
    desc: 'The base rate of Aces to pounds, dollars and euros; £ equivalents are always shown at this rate (18 MON-004).',
    src: '02 §4.5; 18 MON-004',
  }),
  k('4.5', '17', 'aces.coin_list_rate', 20, 'coins', {
    client: true,
    desc: 'Coins to the Ace on the price list. A price list only: Aces never become Coins.',
    src: '02 §4.5',
    ...F,
    note: 'Coins to the Ace; a price list only',
  }),
  k('4.5', '17', 'market.fee_rate', 0.05, 'share', {
    ...F,
    client: true,
    desc: "Seller's fee on marketplace and auction sales.",
    src: '02 §4.5; h12-08',
  }),
  k('4.5', '17', 'biz.game_cut_rate', 0.1, 'share', {
    ...F,
    client: true,
    desc: "The game's cut of everything customers pay a player-owned business. The founder's decision.",
    src: '02 §4.5; r03-03, R-52',
  }),
  k('4.5', '17', 'biz.cut_fund_share', 0.5, 'share', {
    desc: "Share of the game's cut that goes to the grassroots fund; the rest leaves the economy.",
    src: '02 §4.5',
  }),
  k('4.5', '17', 'coins.weekly_anchor', 3000, 'coins', {
    desc: 'Coins an active adult amateur earns in a week at launch.',
    src: '02 §4.5; h12-25',
  }),
  k('4.5', '17', 'econ.dormant_after_days', 182, 'days', {
    desc: "Days without play after which an account's Coins go into the locker (kept in full).",
    src: '02 §4.5; ruling K6',
  }),
  k('4.5', '17', 'econ.settled_after_days_played', 7, 'days', {
    desc: 'Different days played for an account to count as settled (Coins in play).',
    src: '02 §4.5; ECO-007',
  }),
  k('4.5', '18', 'limits.u18_monthly_spend_gbp', 20, 'gbp', {
    type: 'numeric',
    client: true,
    desc: 'Monthly spending cap for an under-18 account once a parent switches spending on; it can be lowered on the Parent page.',
    src: '02 §4.5; R-51',
    ...F,
    note: 'Can be lowered on the Parent page; applies once a parent switches spending on',
  }),
  k('4.5', '17', 'pmc.entry_fee_max', 300, 'coins', {
    client: true,
    desc: 'Highest entry fee of a player-made competition, in Coins.',
    src: '02 §4.5; R-10',
  }),
  k('4.5', '17', 'eng.checkin_coins', 30, 'coins', {
    client: true,
    desc: 'Coins for the daily check-in.',
    src: '02 §4.5; ECO-015',
  }),
  k('4.5', '17', 'referral.coins', 300, 'coins', {
    client: true,
    desc: 'Coins to the referrer and to the new player for a referral.',
    src: '02 §4.5; ECO-015',
    note: "To the referrer and to the new player; the referrer's reward stops after 10 friends",
  }),

  // ---------------------------------------------------------------- 4.7 Safety and wellbeing
  k('4.7', '20', 'notify.daily_max', 3, 'count', {
    client: true,
    desc: 'Push notifications a day at most (player-adjustable; 0 means none but Account and safety).',
    src: '02 §4.7 and §4.16; ENG-153',
    range: [0, 10],
    note: 'Player-adjustable; 0 means no pushes but Account and safety',
  }),
  k('4.7', '18', 'limits.adult_self_limit_gbp', null, 'gbp', {
    type: 'numeric',
    client: true,
    desc: "An adult's own monthly spending limit. Off (null) by default.",
    src: '02 §4.7; R-51',
    note: 'Off by default',
  }),
  k('4.7', '18', 'limits.adult_check_thresholds_gbp', [100, 250], 'gbp', {
    ...F,
    desc: 'Monthly spending at which an adult gets a gentle check.',
    src: '02 §4.7; R-51',
  }),
  k('4.7', '18', 'limits.u18_spending_enabled_default', false, 'boolean', {
    ...F,
    desc: 'An under-18 can spend nothing until a parent switches spending on.',
    src: '02 §4.7; R-51',
  }),
  k('4.7', '18', 'limits.u18_paid_preparation', false, 'boolean', {
    ...F,
    desc: 'An under-18 account never buys preparation with Aces, even once a parent switches spending on.',
    src: '02 §4.7; MON-094',
  }),

  // ---------------------------------------------------------------- 4.8 Caddies
  k('4.8', '14', 'caddie.rounds_to_level.l2', 10, 'rounds', {
    client: true,
    desc: 'Rounds together in total to reach caddie level 2.',
    src: '02 §4.8; c05',
  }),
  k('4.8', '14', 'caddie.rounds_to_level.l3', 40, 'rounds', {
    client: true,
    desc: 'Rounds together in total to reach caddie level 3.',
    src: '02 §4.8; c05',
  }),
  k('4.8', '14', 'caddie.rounds_to_level.l4', 100, 'rounds', {
    client: true,
    desc: 'Rounds together in total to reach caddie level 4.',
    src: '02 §4.8; c05',
  }),
  k('4.8', '14', 'caddie.rounds_to_level.l5', 200, 'rounds', {
    client: true,
    desc: 'Rounds together in total to reach caddie level 5.',
    src: '02 §4.8; c05',
  }),
  k('4.8', '14', 'caddie.upgrade_cost.l2', 5000, 'coins', {
    client: true,
    desc: 'Coins to upgrade a caddie to level 2.',
    src: '02 §4.8; c05 (range ±40%)',
    range: [3000, 7000],
    note: 'Range ±40%',
  }),
  k('4.8', '14', 'caddie.upgrade_cost.l3', 12000, 'coins', {
    client: true,
    desc: 'Coins to upgrade a caddie to level 3.',
    src: '02 §4.8; c05 (range ±40%)',
    range: [7200, 16800],
    note: 'Range ±40%',
  }),
  k('4.8', '14', 'caddie.upgrade_cost.l4', 25000, 'coins', {
    client: true,
    desc: 'Coins to upgrade a caddie to level 4.',
    src: '02 §4.8; c05 (range ±40%)',
    range: [15000, 35000],
    note: 'Range ±40%',
  }),
  k('4.8', '14', 'caddie.upgrade_cost.l5', 45000, 'coins', {
    client: true,
    desc: 'Coins to upgrade a caddie to level 5.',
    src: '02 §4.8; c05 (range ±40%)',
    range: [27000, 63000],
    note: 'Range ±40%',
  }),
  k('4.8', '14', 'caddie.share_min', 0.05, 'share', {
    ...F,
    client: true,
    desc: 'Least share of prize money a professional may pay the caddie.',
    src: '02 §4.8; c07',
  }),
  k('4.8', '14', 'caddie.share_max', 0.1, 'share', {
    ...F,
    client: true,
    desc: 'Most share of prize money a professional may pay the caddie.',
    src: '02 §4.8; c07',
  }),
  k('4.8', '10', 'caddie.max_effect_shots', 2.0, 'shots', {
    ...F,
    type: 'numeric',
    desc: 'Most that caddie levels 1 to 5 may be worth, in shots a round, in any band.',
    src: '02 §4.8; c27',
  }),
  // 2.0 as in the engine's defaults, which hold every band to caddie.max_effect_shots; the
  // parameter set of version 0.5.1 holds 2.5 (`10` §4; c64; decision record 0194)
  k('4.8', '10', 'caddie.max_effect_shots_weaker', 2.0, 'shots', {
    ...F,
    type: 'numeric',
    desc: 'Most that caddie levels 1 to 5 may be worth, in shots a round, to a band weaker than band 10.',
    src: '02 §4.8; c64',
  }),
  k('4.8', '10', 'caddie.level_target_shots.l2', 0.4, 'shots', {
    desc: 'Target worth of a level 2 caddie against a Trainee, shots a round, mid-handicap golfer.',
    src: '02 §4.8',
  }),
  k('4.8', '10', 'caddie.level_target_shots.l3', 0.8, 'shots', {
    desc: 'Target worth of a level 3 caddie against a Trainee.',
    src: '02 §4.8',
  }),
  k('4.8', '10', 'caddie.level_target_shots.l4', 1.2, 'shots', {
    desc: 'Target worth of a level 4 caddie against a Trainee.',
    src: '02 §4.8',
  }),
  k('4.8', '10', 'caddie.target_effect_shots', 1.6, 'shots', {
    desc: 'Target worth of a level 5 caddie against a Trainee.',
    src: '02 §4.8',
  }),
  k('4.8', '14', 'caddie.book_min_level', 4, 'level', {
    client: true,
    desc: "Caddie level from which the caddie's book is offered.",
    src: '02 §4.8 and §4.16; CMP-481',
    ...F,
    range: [3, 5],
  }),
  k('4.8', '14', 'caddie.book_fee_coins', 200, 'coins', {
    client: true,
    desc: "Coins for the caddie's book for a course and tee set. Coins only, never Aces.",
    src: '02 §4.8 and §4.16; CMP-481',
    ...F,
    range: [0, 1000],
    note: 'A course and tee set, Coins only',
  }),

  // ---------------------------------------------------------------- 4.9 Businesses and club extras
  k('4.9', '35', 'biz.owner.max_seats', 3, 'count', {
    ...F,
    client: true,
    desc: 'Most owner or partner seats one account may hold across the business types.',
    src: '02 §4.9; r06-03',
  }),

  // ---------------------------------------------------------------- 4.10 Land and courses
  k('4.10', '12', 'clubs.redesign_days', 5, 'days', {
    client: true,
    desc: 'Real days to redesign a course that is already open.',
    src: '02 §4.10; ruling H26',
    range: [3, 10],
  }),
  k('4.10', '12', 'clubs.redesign_cost_share', 0.5, 'share', {
    desc: 'Share of base price ÷ 18 charged for each hole changed in a redesign (Standard build budget).',
    src: '02 §4.10; CLB-101',
    ...F,
    range: [0.2, 1.0],
  }),

  // ---------------------------------------------------------------- 4.11 Family mode
  k('4.11', '37', 'family.max_juniors.birdie', 1, 'count', {
    client: true,
    desc: 'Family juniors at the same time on the Birdie level.',
    src: '02 §4.11 and §4.15; FAM-002; L10',
    ...F,
    range: [1, 3],
  }),
  k('4.11', '37', 'family.max_juniors.eagle', 2, 'count', {
    client: true,
    desc: 'Family juniors at the same time on the Eagle level.',
    src: '02 §4.11 and §4.15; FAM-002; L10',
    ...F,
    range: [1, 3],
  }),
  k('4.11', '37', 'family.max_juniors.albatross', 3, 'count', {
    client: true,
    desc: 'Family juniors at the same time on the Albatross level.',
    src: '02 §4.11 and §4.15; FAM-002; L10',
    ...F,
    range: [1, 3],
  }),
  k('4.11', '37', 'family.handover_min_gap_days', 228, 'days', {
    desc: 'Least days between two Handovers of one account.',
    src: '02 §4.11 and §4.15; FAM-080',
    ...F,
    range: [0, 455],
  }),
  k('4.11', '37', 'family.head_start_learning_bonus', 0.05, 'share', {
    desc: "Faster learning for a family junior's head start, inside the one cap.",
    src: '02 §4.11; r07-08',
    ...F,
    note: 'Inside the one cap',
  }),

  // ---------------------------------------------------------------- 4.12 Views and watching
  k('4.12', '38', 'launch.broadcast_view_max_wait_days', 91, 'days', {
    desc: 'Longest wait for Broadcast View step 1 before launching with the 2D game.',
    src: '02 §4.12; r01-02, R-57',
  }),
  k('4.12', '38', 'holemap.min_fps', 50, 'fps', {
    client: true,
    desc: 'Least measured frame rate of the Hole Map (aim 60).',
    src: '02 §4.12; c09',
  }),
  k('4.12', '22', 'safety.watch_takedown_max_seconds', 60, 'seconds', {
    desc: 'A watch link, Clip or shared page that is taken down is gone everywhere within this many seconds.',
    src: '02 §4.12; ruling H16',
    range: [10, 300],
  }),

  // ---------------------------------------------------------------- 4.13 The build
  k('4.13', '34', 'docs.claude_spend_warning_gbp', 100, 'gbp', {
    desc: 'Claude spending in a calendar month that triggers the warning.',
    src: '02 §4.13; r09-03',
    range: [50, 200],
  }),

  // ---------------------------------------------------------------- 4.15 The fifth rulings
  k('4.15', '23', 'ui.choice_depth', 'standard', 'option', {
    client: true,
    desc: 'Default choice depth.',
    src: '02 §4.15; ruling L1',
    options: ['simple', 'standard', 'full'],
  }),
  k('4.15', '23', 'ui.choice_depth_under18', 'simple', 'option', {
    client: true,
    desc: 'Choice depth an under-18 account starts with.',
    src: '02 §4.15; ruling L1',
    options: ['simple', 'standard', 'full'],
  }),
  k('4.15', '23', 'ui.explain_shots', 'off', 'option', {
    client: true,
    desc: 'Learning mode default.',
    src: '02 §4.15; SCR-063; ruling L3',
    options: ['off', 'my_shots'],
  }),
  k('4.15', '23', 'ui.explain_hold_seconds', 5, 'seconds', {
    client: true,
    desc: 'Seconds the explanation is held on screen.',
    src: '02 §4.15; ruling L3',
    range: [2, 10],
  }),
  k('4.15', '23', 'ui.pattern_note_seconds', 2.5, 'seconds', {
    client: true,
    desc: 'Seconds the change note beside the pattern is shown.',
    src: '02 §4.15; SCR-044; ruling L11',
    range: [1, 5],
  }),
  k('4.15', '14', 'learn.auto_rounds', 3, 'rounds', {
    client: true,
    desc: 'Rounds for which learning mode is on for a new player.',
    src: '02 §4.15; CMP-339',
    range: [0, 10],
  }),
  k('4.15', '10', 'focus.routine_saving_max', 0.25, 'share', {
    desc: 'Most a well-grooved routine saves of its own focus cost.',
    src: '02 §4.15; SIM-270; ruling L12',
    ...F,
    range: [0, 0.4],
  }),
  k('4.15', '20', 'autoplay.min_days', 1, 'days', {
    client: true,
    desc: 'Shortest Autoplay period in days.',
    src: '02 §4.15; ENG-219',
  }),
  k('4.15', '20', 'autoplay.max_days', 91, 'days', {
    client: true,
    desc: 'Longest Autoplay period in days: up to a season, 13 weeks.',
    src: '02 §4.15; ENG-219; c50',
    range: [7, 91],
  }),
  k('4.15', '20', 'autoplay.entry_budget_coins_week', 0, 'coins', {
    client: true,
    desc: "Autoplay's weekly entry budget in Coins (0 = free entries only); the player sets it, up to the ceiling.",
    src: '02 §4.15; ENG-222 (never above autoplay.entry_budget_ceiling_coins_week, whose range is 0–10,000)',
    ...F,
    range: [0, 10000],
    note: '0 means free entries only; the player sets it, up to the ceiling',
  }),
  k('4.15', '20', 'autoplay.entry_budget_ceiling_coins_week', 3000, 'coins', {
    client: true,
    desc: "Ceiling of Autoplay's weekly entry budget.",
    src: '02 §4.15; ENG-222',
    ...F,
    range: [0, 10000],
  }),
  k('4.15', '20', 'autoplay.notify_level', 'results', 'option', {
    client: true,
    desc: 'Default notifications while on Autoplay.',
    src: '02 §4.15; ENG-224',
    options: ['everything', 'results', 'needs_me', 'nothing'],
  }),
  k('4.15', '20', 'longplay.min_offers', 6, 'count', {
    desc: "Things on the What's on list at least.",
    src: '02 §4.15; ENG-227; ruling L7',
    range: [3, 10],
  }),
  k('4.15', '20', 'longplay.club_36_days_per_month', 1, 'days', {
    desc: 'Club 36-hole days a month (0 switches the format off).',
    src: '02 §4.15; ENG-228',
    range: [0, 2],
    note: '0 switches the format off',
  }),
  k('4.15', '20', 'longplay.group_max', 4, 'count', {
    desc: 'Golfers who stay together between rounds.',
    src: '02 §4.15; ENG-229',
  }),
  k('4.15', '16', 'equipment.ball_misfit_max_pct', 4, 'percent', {
    desc: 'Most a badly fitting ball takes off its two ratings.',
    src: '02 §4.15; EQP-033; ruling L6',
    ...F,
    range: [0, 6],
  }),
  k('4.15', '16', 'ai.ball_fit_min', 90, 'points', {
    desc: 'Least ball fit of any computer-run golfer.',
    src: '02 §4.15; EQP-035',
    ...F,
    range: [80, 95],
  }),
  k('4.15', '14', 'skills.long_grid_width_yd', 40, 'yd', {
    ...F,
    client: true,
    desc: 'Width of the fairway grid in Long.',
    src: '02 §4.15; CMP-451; ruling L9',
  }),
  k('4.15', '17', 'fund.beginner_cup_field', 40, 'count', {
    desc: "Field size of a beginners' cup.",
    src: '02 §4.15; ECO-082',
  }),
  k('4.15', '05', 'data.autoplay_retention_seasons', 2, 'seasons', {
    desc: 'Seasons Autoplay rows are kept.',
    src: '02 §4.15',
  }),
  k('4.15', '05', 'data.play_group_retention_days', 30, 'days', {
    desc: 'Days play-group rows are kept.',
    src: '02 §4.15',
  }),
  k('4.15', '27', 'analytics.learning_open_events_per_round', 3, 'count', {
    desc: 'Learning-open events sent a round at most.',
    src: '02 §4.15',
  }),

  // ---------------------------------------------------------------- 4.16 Kit research (ruling L8)
  k('4.16', '35', 'biz.research.room_per_lab_level', 6, 'points', {
    desc: 'Lab room for each level of the Research lab.',
    src: '02 §4.16; BIZ-291',
    ...F,
    range: [3, 10],
  }),
  k('4.16', '35', 'biz.research.room_per_post_level', 4, 'points', {
    desc: 'Lab room for each level of the research post.',
    src: '02 §4.16; BIZ-291',
    ...F,
    range: [2, 8],
  }),
  k('4.16', '16', 'equipment.tradeoff_sum.driver', 180, 'points', {
    desc: 'Base trade-off sum of a driver design (two ratings).',
    src: '02 §4.16; EQP-230',
    ...F,
    range: [170, 190],
  }),
  k('4.16', '16', 'equipment.tradeoff_sum.fairway_wood', 180, 'points', {
    desc: 'Base trade-off sum of a fairway wood design (two ratings).',
    src: '02 §4.16; EQP-230',
    ...F,
    range: [170, 190],
  }),
  k('4.16', '16', 'equipment.tradeoff_sum.hybrid', 180, 'points', {
    desc: 'Base trade-off sum of a hybrid design (two ratings).',
    src: '02 §4.16; EQP-230',
    ...F,
    range: [170, 190],
  }),
  k('4.16', '16', 'equipment.tradeoff_sum.waterproofs', 180, 'points', {
    desc: 'Base trade-off sum of a waterproofs design (two ratings).',
    src: '02 §4.16; EQP-230',
    ...F,
    range: [170, 190],
  }),
  k('4.16', '16', 'equipment.tradeoff_sum.thermal_layer', 180, 'points', {
    desc: 'Base trade-off sum of a thermal layer design (two ratings).',
    src: '02 §4.16; EQP-230',
    ...F,
    range: [170, 190],
  }),
  k('4.16', '16', 'equipment.tradeoff_sum.irons', 270, 'points', {
    desc: 'Base trade-off sum of an irons design (three ratings).',
    src: '02 §4.16; EQP-230',
    ...F,
    range: [255, 285],
  }),
  k('4.16', '16', 'equipment.tradeoff_sum.wedge', 270, 'points', {
    desc: 'Base trade-off sum of a wedge design (three ratings).',
    src: '02 §4.16; EQP-230',
    ...F,
    range: [255, 285],
  }),
  k('4.16', '16', 'equipment.tradeoff_sum.ball', 270, 'points', {
    desc: 'Base trade-off sum of a ball design (three ratings).',
    src: '02 §4.16; EQP-230',
    ...F,
    range: [255, 285],
  }),
  k('4.16', '16', 'equipment.tradeoff_sum_hard.driver', 190, 'points', {
    desc: "Most that fields may lift a driver's sum to.",
    src: '02 §4.16; EQP-230 (from the base sum up to that figure)',
    ...F,
    range: [180, 190],
    note: 'The most fields may lift the sum to; from the base sum up to this figure',
  }),
  k('4.16', '16', 'equipment.tradeoff_sum_hard.fairway_wood', 190, 'points', {
    desc: "Most that fields may lift a fairway wood's sum to.",
    src: '02 §4.16; EQP-230',
    ...F,
    range: [180, 190],
    note: 'The most fields may lift the sum to; from the base sum up to this figure',
  }),
  k('4.16', '16', 'equipment.tradeoff_sum_hard.hybrid', 190, 'points', {
    desc: "Most that fields may lift a hybrid's sum to.",
    src: '02 §4.16; EQP-230',
    ...F,
    range: [180, 190],
    note: 'The most fields may lift the sum to; from the base sum up to this figure',
  }),
  k('4.16', '16', 'equipment.tradeoff_sum_hard.waterproofs', 190, 'points', {
    desc: "Most that fields may lift a waterproofs design's sum to.",
    src: '02 §4.16; EQP-230',
    ...F,
    range: [180, 190],
    note: 'The most fields may lift the sum to; from the base sum up to this figure',
  }),
  k('4.16', '16', 'equipment.tradeoff_sum_hard.thermal_layer', 190, 'points', {
    desc: "Most that fields may lift a thermal layer's sum to.",
    src: '02 §4.16; EQP-230',
    ...F,
    range: [180, 190],
    note: 'The most fields may lift the sum to; from the base sum up to this figure',
  }),
  k('4.16', '16', 'equipment.tradeoff_sum_hard.irons', 285, 'points', {
    desc: "Most that fields may lift an irons design's sum to.",
    src: '02 §4.16; EQP-230',
    ...F,
    range: [270, 285],
    note: 'The most fields may lift the sum to; from the base sum up to this figure',
  }),
  k('4.16', '16', 'equipment.tradeoff_sum_hard.wedge', 285, 'points', {
    desc: "Most that fields may lift a wedge design's sum to.",
    src: '02 §4.16; EQP-230',
    ...F,
    range: [270, 285],
    note: 'The most fields may lift the sum to; from the base sum up to this figure',
  }),
  k('4.16', '16', 'equipment.tradeoff_sum_hard.ball', 285, 'points', {
    desc: "Most that fields may lift a ball design's sum to.",
    src: '02 §4.16; EQP-230',
    ...F,
    range: [270, 285],
    note: 'The most fields may lift the sum to; from the base sum up to this figure',
  }),
  k('4.16', '16', 'equipment.ball_tradeoff_sum_max', 180, 'points', {
    desc: "Base sum of a ball's distance and spin, as a pair.",
    src: '02 §4.16; EQP-011',
    ...F,
    range: [170, 190],
  }),
  k('4.16', '16', 'equipment.tradeoff_sum_hard.ball_pair', 186, 'points', {
    desc: "Most that fields may lift a ball's distance-and-spin pair to.",
    src: '02 §4.16; EQP-230',
    ...F,
    note: "The ball's distance and spin, as a pair",
  }),
  k('4.16', '35', 'maker.design.field_slots', 2, 'count', {
    desc: 'Fields of its own line in one design.',
    src: '02 §4.16; BIZ-172',
    ...F,
    range: [1, 3],
  }),
  k('4.16', '35', 'tech.field.gain_points.s', 3, 'points', {
    ...F,
    desc: 'Paper gain of a small field.',
    src: '02 §4.16; BIZ-292',
  }),
  k('4.16', '35', 'tech.field.gain_points.m', 5, 'points', {
    ...F,
    desc: 'Paper gain of a medium field.',
    src: '02 §4.16; BIZ-292',
  }),
  k('4.16', '35', 'tech.field.gain_points.l', 8, 'points', {
    ...F,
    desc: 'Paper gain of a large field.',
    src: '02 §4.16; BIZ-292',
  }),
  k('4.16', '35', 'tech.field.cost_points.s', 2, 'points', {
    ...F,
    desc: 'Printed trade-off of a small field paid in points.',
    src: '02 §4.16; BIZ-292',
  }),
  k('4.16', '35', 'tech.field.cost_points.m', 3, 'points', {
    ...F,
    desc: 'Printed trade-off of a medium field paid in points.',
    src: '02 §4.16; BIZ-292',
  }),
  k('4.16', '35', 'tech.field.cost_points.l', 5, 'points', {
    ...F,
    desc: 'Printed trade-off of a large field paid in points.',
    src: '02 §4.16; BIZ-292',
  }),
  k('4.16', '35', 'tech.field.cost_durability.s', 0.04, 'share', {
    ...F,
    desc: 'Printed trade-off of a small field paid in durability.',
    src: '02 §4.16; BIZ-292',
  }),
  k('4.16', '35', 'tech.field.cost_durability.m', 0.07, 'share', {
    ...F,
    desc: 'Printed trade-off of a medium field paid in durability.',
    src: '02 §4.16; BIZ-292',
  }),
  k('4.16', '35', 'tech.field.cost_durability.l', 0.1, 'share', {
    ...F,
    desc: 'Printed trade-off of a large field paid in durability.',
    src: '02 §4.16; BIZ-292',
  }),
  k('4.16', '35', 'tech.field.cost_unit_share.s', 0.02, 'share', {
    ...F,
    desc: 'Printed trade-off of a small field paid in unit cost.',
    src: '02 §4.16; BIZ-292',
  }),
  k('4.16', '35', 'tech.field.cost_unit_share.m', 0.03, 'share', {
    ...F,
    desc: 'Printed trade-off of a medium field paid in unit cost.',
    src: '02 §4.16; BIZ-292',
  }),
  k('4.16', '35', 'tech.field.cost_unit_share.l', 0.05, 'share', {
    ...F,
    desc: 'Printed trade-off of a large field paid in unit cost.',
    src: '02 §4.16; BIZ-292',
  }),
  k('4.16', '35', 'tech.field.cost_stock_fit.s', 1, 'points', {
    ...F,
    desc: 'Printed trade-off of a small field paid in stock fit.',
    src: '02 §4.16; BIZ-292',
  }),
  k('4.16', '35', 'tech.field.cost_stock_fit.m', 2, 'points', {
    ...F,
    desc: 'Printed trade-off of a medium field paid in stock fit.',
    src: '02 §4.16; BIZ-292',
  }),
  k('4.16', '35', 'tech.field.cost_stock_fit.l', 3, 'points', {
    ...F,
    desc: 'Printed trade-off of a large field paid in stock fit.',
    src: '02 §4.16; BIZ-292',
  }),
  k('4.16', '35', 'tech.field.price_mult.s', 1.0, 'multiplier', {
    ...F,
    type: 'numeric',
    desc: 'Price multiple of a small field.',
    src: '02 §4.16; BIZ-292',
  }),
  k('4.16', '35', 'tech.field.price_mult.m', 1.5, 'multiplier', {
    ...F,
    desc: 'Price multiple of a medium field.',
    src: '02 §4.16; BIZ-292',
  }),
  k('4.16', '35', 'tech.field.price_mult.l', 2.5, 'multiplier', {
    ...F,
    desc: 'Price multiple of a large field.',
    src: '02 §4.16; BIZ-292',
  }),
  k('4.16', '35', 'tech.yield_band.proven', { low: 1.0, high: 1.0 }, 'multiplier', {
    ...F,
    desc: "Band { low, high } a proven field's true yield is drawn from.",
    src: '02 §4.16; 35 §4 ("each a low and a high"); BIZ-292',
  }),
  k('4.16', '35', 'tech.yield_band.likely', { low: 0.7, high: 1.2 }, 'multiplier', {
    ...F,
    desc: "Band { low, high } a likely field's true yield is drawn from.",
    src: '02 §4.16; 35 §4 ("each a low and a high"); BIZ-292',
  }),
  k('4.16', '35', 'tech.yield_band.plausible', { low: 0.4, high: 1.3 }, 'multiplier', {
    ...F,
    desc: "Band { low, high } a plausible field's true yield is drawn from.",
    src: '02 §4.16; 35 §4 ("each a low and a high"); BIZ-292',
  }),
  k('4.16', '35', 'tech.yield_band.speculative', { low: 0.0, high: 1.5 }, 'multiplier', {
    ...F,
    desc: "Band { low, high } a speculative field's true yield is drawn from.",
    src: '02 §4.16; 35 §4 ("each a low and a high"); BIZ-292',
  }),
  k('4.16', '35', 'tech.field.truth_level', 3, 'level', {
    desc: 'Field level at which a maker learns the true yield and may fit the field.',
    src: '02 §4.16; BIZ-292',
    ...F,
    range: [2, 5],
  }),
  k('4.16', '35', 'tech.test.study_factor', 0.7, 'share', {
    desc: "What one robot study leaves of a belief band's half-width.",
    src: '02 §4.16; BIZ-293',
    ...F,
    range: [0.5, 0.9],
  }),
  k('4.16', '35', 'tech.test.trial_factor', 0.5, 'share', {
    desc: "What one player trial leaves of a belief band's half-width.",
    src: '02 §4.16; BIZ-293',
    ...F,
    range: [0.3, 0.8],
  }),
  k('4.16', '35', 'maker.proto.study_max', 4, 'count', {
    desc: 'Robot studies a field at most.',
    src: '02 §4.16; BIZ-293',
  }),
  k('4.16', '35', 'maker.proto.study_coins', 300, 'coins', {
    desc: 'Coins for a robot study.',
    src: '02 §4.16; BIZ-293',
  }),
  k('4.16', '35', 'maker.proto.study_coins_shared', 600, 'coins', {
    desc: 'Coins for a robot study on the shared robot.',
    src: '02 §4.16; BIZ-293',
  }),
  k('4.16', '35', 'maker.proto.trial_max', 2, 'count', {
    desc: 'Player trials a field at most.',
    src: '02 §4.16; BIZ-293',
  }),
  k('4.16', '35', 'maker.proto.trial_coins', 500, 'coins', {
    desc: 'Coins for a player trial.',
    src: '02 §4.16; BIZ-293',
  }),
  k('4.16', '35', 'maker.proto.trial_rounds', 10, 'rounds', {
    desc: 'Counted rounds in a player trial.',
    src: '02 §4.16; BIZ-293',
  }),
  k('4.16', '35', 'maker.proto.trial_min_days', 7, 'days', {
    desc: 'Shortest player trial in days.',
    src: '02 §4.16; BIZ-293',
  }),
  k('4.16', '35', 'maker.proto.trial_max_days', 28, 'days', {
    desc: 'Longest player trial in days.',
    src: '02 §4.16; BIZ-293',
  }),
  k('4.16', '35', 'biz.research.bring_back_share', 0.25, 'share', {
    desc: 'Share of list price to bring a shelved level back.',
    src: '02 §4.16; BIZ-294',
    ...F,
    range: [0.1, 0.5],
  }),
  k('4.16', '35', 'biz.research.bring_back_days_per_level', 1, 'days', {
    desc: 'Real days a level to bring a shelved level back.',
    src: '02 §4.16; BIZ-294',
  }),
  k('4.16', '35', 'biz.research.retool_share.same_line', 0.5, 'share', {
    desc: 'Share of list price that re-tooling returns as research credit, same line.',
    src: '02 §4.16; BIZ-294',
    ...F,
    range: [0, 0.9],
  }),
  k('4.16', '35', 'biz.research.retool_share.other_line', 0.25, 'share', {
    desc: 'Share of list price that re-tooling returns as research credit, other line.',
    src: '02 §4.16; BIZ-294',
    ...F,
    range: [0, 0.9],
  }),
  k('4.16', '35', 'biz.research.retool_share.banned', 0.75, 'share', {
    desc: 'Share of list price that re-tooling returns as research credit, banned field.',
    src: '02 §4.16; BIZ-294',
    ...F,
    range: [0, 0.9],
  }),
  k('4.16', '35', 'tech.common_after_seasons', 6, 'seasons', {
    desc: 'Seasons after which a field becomes common knowledge.',
    src: '02 §4.16; BIZ-294',
    range: [3, 12],
  }),
  k('4.16', '35', 'tech.common_price_share', 0.5, 'share', {
    desc: 'Price share of a common field.',
    src: '02 §4.16; BIZ-294',
    ...F,
    range: [0.25, 1],
  }),
  k('4.16', '35', 'tech.era.open_season.e2', 3, 'seasons', {
    desc: 'Season era 2 opens.',
    src: '02 §4.16; BIZ-295',
    ...F,
    range: [2, 6],
  }),
  k('4.16', '35', 'tech.era.open_season.e3', 6, 'seasons', {
    desc: 'Season era 3 opens.',
    src: '02 §4.16; BIZ-295',
    ...F,
    range: [4, 12],
  }),
  k('4.16', '35', 'tech.era.open_season.e4', 10, 'seasons', {
    desc: 'Season era 4 opens.',
    src: '02 §4.16; BIZ-295',
    ...F,
    range: [6, 20],
  }),
  k('4.16', '35', 'tech.horizon.seasons.base', 1, 'seasons', {
    desc: 'Seasons ahead the Horizon shows, for everyone.',
    src: '02 §4.16; BIZ-295',
  }),
  k('4.16', '35', 'tech.horizon.seasons.lab3', 2, 'seasons', {
    desc: 'Seasons ahead the Horizon shows with a Research lab of level 3.',
    src: '02 §4.16; BIZ-295',
  }),
  k('4.16', '35', 'tech.horizon.seasons.lab5', 3, 'seasons', {
    desc: 'Seasons ahead the Horizon shows with a Research lab of level 5.',
    src: '02 §4.16; BIZ-295',
  }),
  k('4.16', '35', 'tech.ai_lag_seasons', 2, 'seasons', {
    ...F,
    desc: 'How far computer-run makers follow behind, in seasons.',
    src: '02 §4.16; BIZ-295; SEED-181',
  }),
  k('4.16', '35', 'tech.ai_latest_seasons', 4, 'seasons', {
    ...F,
    desc: "Computer-run makers' latest-field horizon, in seasons.",
    src: '02 §4.16; BIZ-295; SEED-181',
  }),
  k('4.16', '35', 'tech.ai_room', 30, 'points', {
    ...F,
    desc: 'Lab room of a computer-run maker.',
    src: '02 §4.16; BIZ-295; SEED-181',
  }),
  k('4.16', '35', 'tech.ai_field_level', 3, 'level', {
    ...F,
    desc: 'Field level of a computer-run maker.',
    src: '02 §4.16; BIZ-295; SEED-181',
  }),
  k('4.16', '35', 'maker.model.aged_ref_share.s4', 0.85, 'share', {
    ...F,
    desc: "A model's reference price share after 4 seasons on sale.",
    src: '02 §4.16; BIZ-174',
  }),
  k('4.16', '35', 'maker.model.aged_ref_share.s8', 0.7, 'share', {
    ...F,
    desc: "A model's reference price share after 8 seasons on sale.",
    src: '02 §4.16; BIZ-174',
  }),
  k('4.16', '16', 'tech.rules.limit_line.watched', 1.1, 'multiplier', {
    desc: 'True yield above which the Kit Panel limits a watched field.',
    src: '02 §4.16; EQP-231',
    ...F,
    range: [0.5, 1.5],
  }),
  k('4.16', '16', 'tech.rules.limit_line.contested', 0.9, 'multiplier', {
    desc: 'True yield above which the Kit Panel limits a contested field.',
    src: '02 §4.16; EQP-231',
    ...F,
    range: [0.5, 1.5],
  }),
  k('4.16', '16', 'tech.rules.ban_line.contested', 1.2, 'multiplier', {
    desc: 'True yield above which the Kit Panel bans a contested field.',
    src: '02 §4.16; EQP-231 (stays above the contested limit line)',
    ...F,
    range: [0.5, 1.5],
    note: 'Stays above the contested limit line',
  }),
  k('4.16', '16', 'tech.rules.review_after_seasons', 2, 'seasons', {
    desc: 'Seasons after which the rule-makers review a field.',
    src: '02 §4.16; EQP-231',
  }),
  k('4.16', '16', 'tech.rules.response_days', 28, 'days', {
    desc: 'Days makers have to respond to a proposed ruling.',
    src: '02 §4.16; EQP-231',
  }),
  k('4.16', '16', 'tech.rules.case_min_level', 3, 'level', {
    desc: "Least field level for a maker's case.",
    src: '02 §4.16; EQP-231',
  }),
  k('4.16', '16', 'tech.rules.delay_min_cases', 2, 'count', {
    ...F,
    desc: "Makers' cases needed to delay a ruling.",
    src: '02 §4.16; EQP-231',
  }),
  k('4.16', '16', 'tech.rules.delay_min_bag_share_pct', 5, 'percent', {
    ...F,
    desc: 'Least bag share for cases to delay a ruling.',
    src: '02 §4.16; EQP-231',
  }),
  k('4.16', '16', 'tech.rules.soften_min_cases', 3, 'count', {
    ...F,
    desc: "Makers' cases needed to soften a ruling.",
    src: '02 §4.16; EQP-231',
  }),
  k('4.16', '16', 'tech.rules.soften_margin', 0.05, 'share', {
    ...F,
    desc: 'Margin by which cases may soften a ruling.',
    src: '02 §4.16; EQP-231',
  }),
  k('4.16', '35', 'maker.case_coins', 500, 'coins', {
    desc: "Coins to bring a maker's case.",
    src: '02 §4.16; BIZ-299',
    range: [250, 1000],
  }),
  k('4.16', '16', 'equipment.kit_limit.ball_wind_strokes', 0.1, 'strokes', {
    desc: "Most a ball's wind hold may be worth in the wind reference, in strokes.",
    src: '02 §4.16; EQP-110, EQP-234',
    ...F,
    range: [0.05, 0.2],
  }),
  k('4.16', '16', 'equipment.kit_ceiling_wind_ref.wind_mps', 8.0, 'm_per_s', {
    type: 'numeric',
    desc: 'Wind speed of the wind reference.',
    src: '02 §4.16; EQP-110',
    range: [5, 12],
  }),
  k('4.16', '16', 'equipment.kit_ceiling_wind_ref.bearing_deg', 270, 'deg', {
    desc: 'Wind bearing of the wind reference.',
    src: '02 §4.16; EQP-110',
  }),
  k('4.16', '16', 'equipment.durability_mult_max', 1.15, 'multiplier', {
    desc: "Most a field's trade-off may raise wear.",
    src: '02 §4.16; EQP-082',
    ...F,
    range: [1.0, 1.25],
  }),
  k('4.16', '16', 'fitting.stock_fit_min_pct', 80, 'percent', {
    desc: 'Least fit of stock kit.',
    src: '02 §4.16; EQP-025',
    ...F,
    range: [70, 85],
  }),
  k('4.16', '16', 'fitting.fit_span_max_pct', 20, 'percent', {
    desc: 'Widest fit span.',
    src: '02 §4.16; EQP-030',
    ...F,
    range: [10, 30],
  }),
  k('4.16', '16', 'fitting.ball.window_max_pct', 30, 'percent', {
    desc: 'Widest ball fit window.',
    src: '02 §4.16; EQP-033',
    ...F,
    range: [15, 45],
  }),
  k('4.16', '10', 'sim.kit.ball_wind_span', 0.08, 'share', {
    desc: 'Working value of the ball wind-hold input.',
    src: '02 §4.16; SIM-330 (calibration may move it down, never up)',
    ...F,
    range: [0, 0.08],
    note: 'Calibration may move it down, never up',
  }),
  k('4.16', '10', 'sim.kit.wet_hold_span', 0.5, 'share', {
    desc: 'Working value of the wet-hold input.',
    src: '02 §4.16; SIM-330 (down, never up)',
    ...F,
    range: [0, 0.5],
    note: 'Calibration may move it down, never up',
  }),
  k('4.16', '10', 'sim.kit.grip_wet_span', 0.15, 'share', {
    desc: 'Working value of the wet-grip input.',
    src: '02 §4.16; SIM-330 (down, never up)',
    ...F,
    range: [0, 0.15],
    note: 'Calibration may move it down, never up',
  }),
  k('4.16', '10', 'sim.kit.carry_saving_span', 0.1, 'share', {
    desc: 'Working value of the carry-saving input.',
    src: '02 §4.16; SIM-330 (down, never up)',
    ...F,
    range: [0, 0.1],
    note: 'Calibration may move it down, never up',
  }),
  k('4.16', '10', 'sim.kit.breathe_span', 0.5, 'share', {
    desc: 'Working value of the breathable-clothing input.',
    src: '02 §4.16; SIM-330 (down, never up)',
    ...F,
    range: [0, 0.5],
    note: 'Calibration may move it down, never up',
  }),
  k('4.16', '16', 'tech.rangefinder_offset_reduction_pct', 25, 'percent', {
    desc: "Cap on a rangefinder's own info_reduction_pct.",
    src: '02 §4.16; EQP-100',
    range: [10, 40],
  }),
  k('4.16', '35', 'biz.line.addon_setup_coins', 6000, 'coins', {
    desc: 'Coins to add the golf tech line to a maker (closed to new makers since c27).',
    src: '02 §4.16; BIZ-300',
    ...F,
    range: [3000, 12000],
  }),
  k('4.16', '35', 'biz.line.addon_setup_days', 3, 'days', {
    desc: 'Days to add the golf tech line.',
    src: '02 §4.16; BIZ-300',
  }),
  k('4.16', '35', 'biz.setup.ball_maker.aces', 2000, 'aces', {
    client: true,
    desc: 'Aces to open a ball maker (or 20 Coins to the Ace).',
    src: '02 §4.16; BIZ-010, BIZ-296 (±50%)',
    ...F,
    range: [1000, 3000],
    note: 'Range ±50%; or 40,000 Coins at 20 to the Ace',
  }),
  k('4.16', '35', 'biz.setup.clothing_brand.aces', 1500, 'aces', {
    client: true,
    desc: 'Aces to open a clothing brand (or 20 Coins to the Ace).',
    src: '02 §4.16; BIZ-010, BIZ-296 (±50%)',
    ...F,
    range: [750, 2250],
    note: 'Range ±50%; or 30,000 Coins at 20 to the Ace',
  }),
  k('4.16', '35', 'biz.setup.buggy_maker.aces', 1500, 'aces', {
    client: true,
    desc: 'Aces to open a bag and trolley maker (or 20 Coins to the Ace).',
    src: '02 §4.16; BIZ-010, BIZ-296 (±50%)',
    ...F,
    range: [750, 2250],
    note: 'Range ±50%; or 30,000 Coins at 20 to the Ace',
  }),
  k('4.16', '35', 'biz.seed.ball_maker', 9, 'count', {
    ...F,
    desc: 'Computer-run ball makers seeded.',
    src: '02 §4.16; BIZ-130',
  }),
  k('4.16', '35', 'biz.ai_floor.ball_maker', 3, 'count', {
    ...F,
    desc: 'Fewest computer-run ball makers.',
    src: '02 §4.16; BIZ-130',
  }),
  k('4.16', '35', 'biz.seed.clothing_brand', 4, 'count', {
    ...F,
    desc: 'Computer-run clothing brands seeded.',
    src: '02 §4.16; BIZ-130',
  }),
  k('4.16', '35', 'biz.ai_floor.clothing_brand', 2, 'count', {
    ...F,
    desc: 'Fewest computer-run clothing brands.',
    src: '02 §4.16; BIZ-130',
  }),
  k('4.16', '35', 'biz.seed.buggy_maker', 4, 'count', {
    ...F,
    desc: 'Computer-run bag and trolley makers seeded.',
    src: '02 §4.16; BIZ-130',
  }),
  k('4.16', '35', 'biz.ai_floor.buggy_maker', 2, 'count', {
    ...F,
    desc: 'Fewest computer-run bag and trolley makers.',
    src: '02 §4.16; BIZ-130',
  }),
  k('4.16', '35', 'maker.ai_base_coins_week.ball', 2400, 'coins', {
    ...F,
    desc: 'Weekly base Coins of a computer-run ball maker.',
    src: '02 §4.16; BIZ-271',
  }),
  k('4.16', '35', 'maker.ai_base_coins_week.clothing', 1800, 'coins', {
    ...F,
    desc: 'Weekly base Coins of a computer-run clothing brand.',
    src: '02 §4.16; BIZ-272',
  }),
  k('4.16', '35', 'maker.ai_base_coins_week.bag_trolley', 2000, 'coins', {
    ...F,
    desc: 'Weekly base Coins of a computer-run bag and trolley maker.',
    src: '02 §4.16; BIZ-276',
  }),
  k('4.16', '35', 'maker.unit_cost_share.ball', { start: 0.6, end: 0.45 }, 'share', {
    desc: "A ball maker's unit cost as a share of the reference price, falling from the first figure to the second.",
    src: '02 §4.16; 35 §4; BIZ-271',
    ...F,
    range: [0.4, 0.8],
    note: 'Falling from the first to the second',
  }),
  k('4.16', '35', 'maker.unit_cost_share.clothing', { start: 0.55, end: 0.45 }, 'share', {
    desc: "A clothing brand's unit cost as a share of the reference price, falling from the first figure to the second.",
    src: '02 §4.16; 35 §4; BIZ-272',
    ...F,
    range: [0.4, 0.8],
    note: 'Falling from the first to the second',
  }),
  k('4.16', '35', 'maker.unit_cost_share.bag_trolley', { start: 0.6, end: 0.45 }, 'share', {
    desc: "A bag and trolley maker's unit cost as a share of the reference price, falling from the first figure to the second.",
    src: '02 §4.16; 35 §4; BIZ-276',
    ...F,
    range: [0.4, 0.8],
    note: 'Falling from the first to the second',
  }),
  k('4.16', '35', 'maker.hours_week.ball', [40, 48, 60, 80, 110], 'hours', {
    desc: "A ball maker's moulding line hours a week, by machine level 1 to 5.",
    src: '02 §4.16; 35 §4; BIZ-271',
    ...F,
    note: 'By machine level 1 to 5',
  }),
  k('4.16', '35', 'maker.hours_week.clothing', [30, 36, 45, 60, 82], 'hours', {
    desc: "A clothing brand's cutting room hours a week, by machine level 1 to 5.",
    src: '02 §4.16; 35 §4; BIZ-272',
    ...F,
    note: 'By machine level 1 to 5',
  }),
  k('4.16', '35', 'maker.hours_week.bag_trolley', [30, 36, 45, 60, 82], 'hours', {
    desc: "A bag and trolley maker's assembly line hours a week, by machine level 1 to 5.",
    src: '02 §4.16; 35 §4; BIZ-276',
    ...F,
    note: 'By machine level 1 to 5',
  }),
  k('4.16', '35', 'tech.patent_seasons', 4, 'seasons', {
    desc: 'Seasons a patent lasts.',
    src: '02 §4.16; BIZ-297',
    ...F,
    range: [2, 8],
  }),
  k('4.16', '35', 'tech.licence.rate_pct_options', [2, 4, 6], 'percent', {
    desc: "Royalty rates offered, per cent of each unit's reference price.",
    src: '02 §4.16; BIZ-297',
    ...F,
    note: "Of each unit's reference price",
  }),
  k('4.16', '35', 'tech.licence.open_max', 3, 'count', {
    ...F,
    desc: 'Licensees on an open licence at most.',
    src: '02 §4.16; BIZ-297',
  }),
  k('4.16', '35', 'tech.licence.max_in', 2, 'count', {
    ...F,
    desc: 'Licences in for one business at most.',
    src: '02 §4.16; BIZ-297; ECO-190',
  }),
  k('4.16', '35', 'biz.abuse.royalty_single_share', 0.5, 'share', {
    ...F,
    desc: 'Share of royalties from one licensee that raises the flag.',
    src: '02 §4.16; BIZ-202',
  }),
  k('4.16', '35', 'biz.abuse.royalty_min_coins', 2000, 'coins', {
    ...F,
    desc: 'Least royalties for the one-licensee flag.',
    src: '02 §4.16; BIZ-202',
  }),
  k('4.16', '16', 'shop.compare_same_points', 5, 'points', {
    client: true,
    desc: 'Points within which two products compare as the same.',
    src: '02 §4.16; EQP-236',
  }),
  k('4.16', '16', 'shop.compare_max_models', 3, 'count', {
    client: true,
    desc: 'Models compared at once at most.',
    src: '02 §4.16; EQP-236',
  }),
  k('4.16', '16', 'shop.tech_badges_max', 4, 'count', {
    client: true,
    desc: 'Tech badges on a product at most.',
    src: '02 §4.16; EQP-236',
  }),
  k('4.16', '35', 'tech.test.min_gap_points', 8, 'points', {
    desc: 'Pass line of a test: least gap in points.',
    src: '02 §4.16; BIZ-305; TST-271',
    ...F,
    range: [4, 16],
  }),
  k('4.16', '35', 'maker.recovery.target_weeks', 26, 'weeks', {
    desc: 'Pass line of a test: weeks for a maker to recover.',
    src: '02 §4.16; BIZ-307; TST-271',
    ...F,
    range: [13, 52],
  }),
  k('4.16', '16', 'clothing.weather_mitigation_max_pct', 40, 'percent', {
    desc: 'Most weather effect clothing can take away.',
    src: '02 §4.16 (stays); EQP-090',
    note: 'It stays; the three other clothing keys are retired',
  }),

  // ---------------------------------------------------------------- 4.16 Stats Room, export, study, books (ruling L15)
  k('4.16', '22', 'export.golf_min_gap_minutes', 60, 'minutes', {
    client: true,
    desc: 'Least minutes between two Export my golf requests.',
    src: '02 §4.16; SAF-120',
    range: [10, 1440],
  }),
  k('4.16', '22', 'export.link_hours', 24, 'hours', {
    client: true,
    desc: 'Hours an export link lasts.',
    src: '02 §4.16; SAF-120',
    range: [1, 72],
  }),
  k('4.16', '22', 'export.build_max_minutes', 10, 'minutes', {
    desc: 'Longest an export may take to build.',
    src: '02 §4.16; SAF-120',
    range: [2, 30],
  }),
  k('4.16', '22', 'export.sheet_max_rows', 1000000, 'count', {
    desc: 'Rows in one export sheet at most.',
    src: '02 §4.16; SAF-120',
    range: [100000, 1048576],
  }),
  k('4.16', '05', 'data.stroke_facts_retention_seasons', 20, 'seasons', {
    desc: 'Seasons stroke facts are kept.',
    src: '02 §4.16; DAT-130',
    range: [4, 40],
  }),
  k('4.16', '18', 'membership.deep_stats_min_tier', 'eagle', 'option', {
    client: true,
    desc: 'Lowest membership level for the finer strokes-gained views.',
    src: '02 §4.16; MON-170',
    options: ['birdie', 'eagle', 'albatross'],
  }),
  k('4.16', '18', 'stats.free_saved_views', 2, 'count', {
    client: true,
    desc: 'Saved Stats Room views for a free player.',
    src: '02 §4.16; MON-170',
    ...F,
    range: [0, 5],
  }),
  k('4.16', '23', 'stats.saved_views_max', 30, 'count', {
    client: true,
    desc: 'Saved views at most.',
    src: '02 §4.16; SCR-074',
    range: [10, 100],
  }),
  k('4.16', '23', 'stats.min_sample', 30, 'strokes', {
    client: true,
    desc: 'Strokes under which a figure is greyed.',
    src: '02 §4.16; SCR-074',
    range: [10, 200],
  }),
  k('4.16', '07', 'study.public_history_min_rounds', 50, 'rounds', {
    desc: "Rounds before a hole's public averages are shown.",
    src: '02 §4.16; CRS-100',
    ...F,
    range: [20, 500],
  }),
  k('4.16', '08', 'study.knowledge_cap', 0.3, 'share', {
    desc: 'Most course knowledge study sessions give.',
    src: '02 §4.16; GOL-180 (never above study.sim_knowledge_cap)',
    ...F,
    range: [0.2, 0.4],
    note: 'Never above study.sim_knowledge_cap',
  }),
  k('4.16', '08', 'study.sim_knowledge_cap', 0.45, 'share', {
    desc: 'Most course knowledge the simulator gives.',
    src: '02 §4.16; GOL-180',
    ...F,
    range: [0.3, 0.6],
  }),
  k('4.16', '14', 'book.max_per_golfer', 100, 'count', {
    client: true,
    desc: 'Course books a golfer may keep.',
    src: '02 §4.16; CMP-480',
    range: [10, 500],
  }),
  k('4.16', '14', 'book.marks_per_hole_max', 6, 'count', {
    client: true,
    desc: 'Marks on one hole of a course book.',
    src: '02 §4.16; CMP-480',
    range: [0, 12],
  }),
  k('4.16', '14', 'reads.notes_min_course_knowledge_with_book', 0.25, 'share', {
    desc: "Course knowledge from which a golfer's notes count, with a course book.",
    src: '02 §4.16; CMP-321, CMP-480; SIM-331',
    ...F,
    range: [0.15, 0.6],
  }),
  k('4.16', '10', 'study.target_effect_shots', 0.4, 'shots', {
    desc: 'Target worth of study, in shots.',
    src: '02 §4.16; SIM-331',
    range: [0.3, 0.5],
  }),
  k('4.16', '10', 'sim.gate.study_max_shots', 0.75, 'shots', {
    desc: 'The study limit, in shots.',
    src: '02 §4.16; SIM-331; TST-270',
    ...F,
    range: [0.5, 0.75],
  }),
  k('4.16', '10', 'sim.gate.study_min_effect_shots', 0.1, 'shots', {
    desc: 'Least effect of study for the gate, in shots.',
    src: '02 §4.16; SIM-331',
    ...F,
    range: [0.05, 0.2],
  }),
  k('4.16', '10', 'sim.gate.study_played_max_shots', 0.05, 'shots', {
    desc: 'Most effect of study on a hole already played, in shots.',
    src: '02 §4.16; SIM-331',
    ...F,
    range: [0, 0.1],
  }),
  k('4.16', '10', 'sim.gate.study_not_worse_shots', 0.02, 'shots', {
    desc: 'Gate line: study is not worse, in shots.',
    src: '02 §4.16; SIM-331',
  }),
  k('4.16', '10', 'sim.gate.study_target_tolerance', 0.2, 'shots', {
    desc: 'Gate tolerance around the study target.',
    src: '02 §4.16; SIM-331',
  }),
  k('4.16', '10', 'sim.gate.study_plan_think_seconds', 8, 'seconds', {
    desc: 'Thinking seconds for a plan in the study gate.',
    src: '02 §4.16; SIM-331',
  }),
  k('4.16', '08', 'ai.away_course_knowledge.club', 0.0, 'share', {
    ...F,
    type: 'numeric',
    desc: 'Course knowledge of a computer-run golfer away from home, club events.',
    src: '02 §4.16; GOL-180',
  }),
  k('4.16', '08', 'ai.away_course_knowledge.county', 0.1, 'share', {
    ...F,
    desc: 'Course knowledge of a computer-run golfer away from home, county events.',
    src: '02 §4.16; GOL-180',
  }),
  k('4.16', '08', 'ai.away_course_knowledge.national', 0.2, 'share', {
    ...F,
    desc: 'Course knowledge of a computer-run golfer away from home, national events.',
    src: '02 §4.16; GOL-180',
  }),
  k('4.16', '08', 'ai.away_course_knowledge.tour', 0.35, 'share', {
    ...F,
    desc: 'Course knowledge of a computer-run golfer away from home, tour events.',
    src: '02 §4.16; GOL-180',
  }),
  k('4.16', '08', 'ai.away_course_knowledge.major', 0.45, 'share', {
    ...F,
    desc: 'Course knowledge of a computer-run golfer away from home, majors.',
    src: '02 §4.16; GOL-180',
  }),

  // ---------------------------------------------------------------- 4.16 Energy bar, looks, flight (L14, L16, L17)
  k('4.16', '08', 'looks.swing_styles_free_min', 3, 'count', {
    client: true,
    desc: 'Swing styles that are always free.',
    src: '02 §4.16; GOL-182',
    range: [3, 10],
  }),
  k('4.16', '24', 'ui.swing_ms', 900, 'ms', {
    client: true,
    desc: 'Length of the drawn swing, the same for every swing style.',
    src: '02 §4.16; DES-572',
  }),
  k('4.16', '24', 'ui.swing_strike_fraction', 0.6, 'share', {
    client: true,
    desc: 'Point of the drawn swing at which the ball is struck.',
    src: '02 §4.16; DES-572',
  }),
  k('4.16', '38', 'flight.min_part_ms', 120, 'ms', {
    client: true,
    desc: 'Shortest part of a drawn ball flight.',
    src: '02 §4.16; VW-231',
  }),
  k('4.16', '38', 'flight.roll_drag', 0.5, 'multiplier', {
    client: true,
    desc: "Drag on a drawn ball's roll.",
    src: '02 §4.16; VW-231',
  }),

  // ---------------------------------------------------------------- 4.16 Friends, communities, the golf world (L18, L19, L21)
  k('4.16', '20', 'society.season_enabled', true, 'boolean', {
    client: true,
    desc: "The founder's switch for society seasons.",
    src: '02 §4.16; ENG-240',
    note: "The founder's switch",
  }),
  k('4.16', '20', 'starter.enabled', true, 'boolean', {
    client: true,
    desc: "The founder's switch for starter groups.",
    src: '02 §4.16; ENG-242',
    note: "The founder's switch",
  }),
  k('4.16', '20', 'society.invite_enabled', true, 'boolean', {
    client: true,
    desc: "The founder's switch for group invites.",
    src: '02 §4.16; ENG-243',
    note: "The founder's switch",
  }),
  k('4.16', '12', 'campaign.enabled', true, 'boolean', {
    client: true,
    desc: "The founder's switch for the club campaign.",
    src: '02 §4.16; CLB-111',
    note: "The founder's switch",
  }),
  k('4.16', '23', 'onboarding.first_shot_target_seconds', 60, 'seconds', {
    client: true,
    desc: "Target seconds to a new player's first shot.",
    src: '02 §4.16; SCR-083; ruling L21',
  }),
  k('4.16', '14', 'onboarding.first_hole_max_strokes', 8, 'strokes', {
    client: true,
    desc: 'Most strokes on the first hole.',
    src: '02 §4.16; CMP-494; ruling L21',
  }),
  k('4.16', '20', 'society.days_per_season', 8, 'days', {
    client: true,
    desc: 'Society days in a society season.',
    src: '02 §4.16; ENG-240',
    range: [4, 12],
  }),
  k('4.16', '20', 'society.best_n', 5, 'count', {
    client: true,
    desc: 'Best days that count in a society season.',
    src: '02 §4.16; ENG-240',
    range: [3, 8],
  }),
  k('4.16', '20', 'society.flight_size', 20, 'count', {
    client: true,
    desc: 'Golfers in a society flight.',
    src: '02 §4.16; ENG-240',
    range: [10, 40],
  }),
  k('4.16', '20', 'society.points_scale', [20, 16, 13, 11, 10], 'points', {
    client: true,
    desc: 'Points by place for the first places (20, 16, 13, 11, 10); after the list, one fewer each place, never under society.points_floor.',
    src: '02 §4.16; 20 §4 (a falling list of 3 to 10 whole numbers, each 1–50); ENG-240; CMP-483',
    range: [1, 50],
    note: 'Then one fewer each place',
  }),
  k('4.16', '20', 'society.points_floor', 1, 'points', {
    client: true,
    desc: 'Points never fall under this.',
    src: '02 §4.16; ENG-240',
  }),
  k('4.16', '20', 'society.day_min_finishers', 4, 'count', {
    client: true,
    desc: 'Finishers for a society day to count.',
    src: '02 §4.16; ENG-240',
  }),
  k('4.16', '20', 'society.season_min_days', 4, 'days', {
    client: true,
    desc: 'Days for a society season to count.',
    src: '02 §4.16; ENG-240',
  }),
  k('4.16', '20', 'society.role_max_terms_running', 2, 'count', {
    client: true,
    desc: 'Terms running in a row in a society role at most.',
    src: '02 §4.16; ENG-241',
  }),
  k('4.16', '20', 'society.nomination_hours', 72, 'hours', {
    client: true,
    desc: 'Hours nominations are open.',
    src: '02 §4.16; ENG-241',
  }),
  k('4.16', '20', 'society.vote_hours', 72, 'hours', {
    client: true,
    desc: 'Hours a society vote is open.',
    src: '02 §4.16; ENG-241',
  }),
  k('4.16', '20', 'society.captain_notices_per_week', 1, 'count', {
    client: true,
    desc: "Captain's notices a week at most.",
    src: '02 §4.16; ENG-241',
  }),
  k('4.16', '20', 'roles.deputy_after_days', 7, 'days', {
    desc: 'Days after which a deputy takes over an absent role.',
    src: '02 §4.16; ENG-241',
    range: [3, 28],
  }),
  k('4.16', '20', 'starter.group_size', 20, 'count', {
    desc: 'Golfers in a starter group.',
    src: '02 §4.16; ENG-242',
    range: [8, 30],
  }),
  k('4.16', '20', 'starter.table_days', 28, 'days', {
    desc: "Days a starter group's table runs.",
    src: '02 §4.16; ENG-242',
  }),
  k('4.16', '20', 'starter.best_rounds', 4, 'rounds', {
    desc: 'Best rounds that count on the starter table.',
    src: '02 §4.16; ENG-242',
  }),
  k('4.16', '20', 'starter.offer_days', 14, 'days', {
    desc: 'Days a starter group is offered.',
    src: '02 §4.16; ENG-242',
  }),
  k('4.16', '20', 'starter.fill_days', 14, 'days', {
    desc: 'Days to fill a starter group.',
    src: '02 §4.16; ENG-242',
  }),
  k('4.16', '20', 'starter.table_min_rows', 8, 'count', {
    desc: 'Least rows on a starter table.',
    src: '02 §4.16; ENG-242',
  }),
  k('4.16', '20', 'starter.channel_close_days', 7, 'days', {
    desc: "Days after which a starter group's channel closes.",
    src: '02 §4.16; ENG-242',
  }),
  k('4.16', '22', 'safety.u18_starter_groups_enabled', false, 'boolean', {
    desc: 'Starter groups for under-18 accounts: built and off until the review of under-18 data (28 LEG-109).',
    src: '02 §4.16; SAF-121',
    ...F,
    note: 'Off until the review of under-18 data (28 LEG-109)',
  }),
  k('4.16', '20', 'society.invite_days', 30, 'days', {
    client: true,
    desc: 'Days a group invite lasts.',
    src: '02 §4.16; ENG-243',
    range: [7, 90],
  }),
  k('4.16', '20', 'society.invite_uses', 60, 'count', {
    client: true,
    desc: 'Uses of a group invite at most.',
    src: '02 §4.16; ENG-243',
    range: [10, 200],
  }),
  k('4.16', '20', 'society.invite_links_per_season', 3, 'count', {
    client: true,
    desc: 'Invite links a season at most.',
    src: '02 §4.16; ENG-243',
  }),
  k('4.16', '14', 'friendly.window_hours', 72, 'hours', {
    client: true,
    desc: 'Hours a friendly window match is open.',
    src: '02 §4.16; CMP-482',
    range: [24, 168],
  }),
  k('4.16', '14', 'friendly.max_open_per_pair', 3, 'count', {
    client: true,
    desc: 'Open friendly matches between two golfers at most.',
    src: '02 §4.16; CMP-482',
    range: [1, 5],
  }),
  k('4.16', '14', 'proam.holes', 9, 'holes', {
    client: true,
    desc: 'Holes in a pro-am.',
    src: '02 §4.16; CMP-484',
  }),
  k('4.16', '14', 'proam.teams', 24, 'count', {
    client: true,
    desc: 'Teams in a pro-am.',
    src: '02 §4.16; CMP-484',
  }),
  k('4.16', '14', 'proam.fame_places_per_season', 2, 'count', {
    client: true,
    desc: 'Fame places in pro-ams a season.',
    src: '02 §4.16; CMP-484',
  }),
  k('4.16', '14', 'rollup.group_size', 4, 'count', {
    client: true,
    desc: 'Golfers in a roll-up group.',
    src: '02 §4.16; CMP-485',
  }),
  k('4.16', '14', 'rollup.table_best_n', 6, 'count', {
    client: true,
    desc: 'Best roll-ups that count on the table.',
    src: '02 §4.16; CMP-485',
  }),
  k('4.16', '12', 'clubs.rollup_slot', { start: '16:00', end: '17:00' }, 'structure', {
    client: true,
    desc: 'The fixed-time slot of the roll-up.',
    src: '02 §4.16; CLB-025',
    note: 'The 16:00 to 17:00 fixed-time slot',
  }),
  k('4.16', '14', 'rangenight.bold_points_mult', 1.5, 'multiplier', {
    client: true,
    desc: 'Points multiple for a bold shot on range night.',
    src: '02 §4.16; CMP-486',
  }),
  k('4.16', '12', 'clubs.range_night_day', 'thursday', 'option', {
    client: true,
    desc: 'Day of the week of range night; the slot is 19:00 to 21:00 local.',
    src: '02 §4.16; 12 §4; CLB-025',
    options: WEEKDAYS,
    note: 'A day of the week; the slot is 19:00 to 21:00 (12 CLB-025)',
  }),
  k('4.16', '12', 'wall.founding_days', 91, 'days', {
    client: true,
    desc: 'Days in which a member counts as a founding member on the Clubhouse Wall.',
    src: '02 §4.16; CLB-110',
  }),
  k('4.16', '12', 'wall.course_record_min_rounds', 20, 'rounds', {
    client: true,
    desc: 'Rounds on a course before a course record is shown.',
    src: '02 §4.16; CLB-110',
  }),
  k('4.16', '12', 'campaign.min_human_members', 6, 'count', {
    client: true,
    desc: 'Human members needed for a club campaign.',
    src: '02 §4.16; CLB-111',
  }),
  k('4.16', '12', 'campaign.vote_days', 7, 'days', {
    client: true,
    desc: 'Days a club campaign vote is open.',
    src: '02 §4.16; CLB-111',
  }),
  k('4.16', '12', 'clubs.hole_in_one_treat_coins', 100, 'coins', {
    client: true,
    desc: 'Coins for the optional hole-in-one treat (0 hides the offer).',
    src: '02 §4.16; CLB-113',
    ...F,
    range: [0, 500],
    note: '0 hides the offer',
  }),
  k('4.16', '19', 'yousaid.max_items', 10, 'count', {
    client: true,
    desc: 'Items on the You said, we did page at most.',
    src: '02 §4.16; MED-250',
  }),
  k('4.16', '19', 'ledger.last_results', 5, 'count', {
    client: true,
    desc: 'Last results shown on the ledger between friends.',
    src: '02 §4.16; MED-252',
  }),
  k('4.16', '36', 'land.made_by_min_reviews', 5, 'count', {
    client: true,
    desc: 'Reviews before a course shows as Made by members.',
    src: '02 §4.16; LND-160',
  }),
  k('4.16', '27', 'analytics.community_min_others', 3, 'count', {
    desc: 'Community measurement: least other players.',
    src: '02 §4.16; ANA-310',
  }),
  k('4.16', '27', 'analytics.community_society_days_min', 4, 'days', {
    desc: 'Community measurement: least society days.',
    src: '02 §4.16; ANA-310',
  }),
  k('4.16', '27', 'analytics.community_survival_min_humans', 4, 'count', {
    desc: 'Community measurement: least humans for a group to survive.',
    src: '02 §4.16; ANA-310',
  }),
  k('4.16', '27', 'analytics.community_away_days', 14, 'days', {
    desc: 'Community measurement: days away.',
    src: '02 §4.16; ANA-310',
  }),
  k('4.16', '27', 'analytics.community_return_days', 7, 'days', {
    desc: 'Community measurement: days to return.',
    src: '02 §4.16; ANA-310',
  }),

  // ---------------------------------------------------------------- 4.16 The rulings on the audits (N1 to N6)
  k('4.16', '09', 'prices.junior_factor', 0.5, 'multiplier', {
    client: true,
    desc: "Price factor of a coach's week and an extra lesson for a golfer under 18.",
    src: '02 §4.16; TRN-193; ruling N2',
    ...F,
    range: [0.25, 1],
  }),
  k('4.16', '07', 'travel.junior_factor', 0.5, 'multiplier', {
    client: true,
    desc: 'Travel price factor for a golfer under 18.',
    src: '02 §4.16; CRS-023; ruling N2',
    ...F,
    range: [0.25, 1],
  }),
  k('4.16', '07', 'travel.tour_event_coins', 300, 'coins', {
    client: true,
    desc: "A tour member's journey to an event of their own tour (the tour's travel desk).",
    src: '02 §4.16; CRS-023; ruling N3',
    ...F,
    range: [0, 2000],
  }),
  k('4.16', '10', 'sim.level_check.rounds', 200, 'rounds', {
    desc: 'Rounds in the anchor check of the playing level.',
    src: '02 §4.16; SIM-160; ruling N6',
  }),
  k('4.16', '10', 'sim.gate.level_anchor_shots', 1.0, 'shots', {
    type: 'numeric',
    desc: "Tolerance of the playing level's anchor check, in shots.",
    src: '02 §4.16; SIM-160; ruling N6',
  }),
  k('4.16', '12', 'clubs.insolvency_caretaker_weeks', 2, 'weeks', {
    desc: 'Weeks a caretaker runs an insolvent club.',
    src: '02 §4.16; CLB-015',
    ...F,
    range: [1, 4],
  }),
  k('4.16', '12', 'clubs.weekly_open_comps_min', 3, 'count', {
    desc: 'Open competitions a club runs a week at least.',
    src: '02 §4.16; CLB-025',
    ...F,
    range: [1, 5],
  }),
  k('4.16', '14', 'field.junior_club_min', 20, 'count', {
    desc: 'Least field of a junior club competition.',
    src: '02 §4.16; CMP-020',
    ...F,
    range: [8, 40],
  }),
  k('4.16', '14', 'event_setup.green_speed_ft.tour', 12.0, 'stimp_ft', {
    ...F,
    type: 'numeric',
    desc: 'Green speed at a tour event, in feet.',
    src: '02 §4.16; CMP-490',
  }),
  k('4.16', '14', 'event_setup.green_speed_ft.major', 13.0, 'stimp_ft', {
    ...F,
    type: 'numeric',
    desc: 'Green speed at a major, in feet.',
    src: '02 §4.16; CMP-490',
  }),
  k('4.16', '14', 'event_setup.rough_mm.tour', 90, 'mm', {
    ...F,
    desc: 'Rough height at a tour event, in millimetres.',
    src: '02 §4.16; CMP-490',
  }),
  k('4.16', '14', 'event_setup.rough_mm.major', 110, 'mm', {
    ...F,
    desc: 'Rough height at a major, in millimetres.',
    src: '02 §4.16; CMP-490',
  }),
  k('4.16', '11', 'rating.neutral.temp_c', 15, 'celsius', {
    ...F,
    type: 'numeric',
    desc: 'Temperature of the conditions in which a tee is rated.',
    src: '02 §4.16; HCP-130',
  }),
  k('4.16', '11', 'rating.neutral.wind_ms', 3.0, 'm_per_s', {
    ...F,
    type: 'numeric',
    desc: 'Wind of the conditions in which a tee is rated.',
    src: '02 §4.16; HCP-130',
  }),
  k('4.16', '11', 'rating.neutral.firmness', 0.5, 'share', {
    ...F,
    desc: 'Firmness of the conditions in which a tee is rated.',
    src: '02 §4.16; HCP-130',
  }),
  k('4.16', '11', 'rating.neutral.green_speed_ft', 10.0, 'stimp_ft', {
    ...F,
    type: 'numeric',
    desc: 'Green speed of the conditions in which a tee is rated.',
    src: '02 §4.16; HCP-130',
  }),
  k('4.16', '09', 'habits.sleep_composure_start', 2, 'points', {
    ...F,
    type: 'numeric',
    desc: 'Composure at the start of a round from good sleep.',
    src: '02 §4.16; TRN-121',
  }),
  k('4.16', '09', 'habits.eat_well_energy_mult', 0.95, 'multiplier', {
    ...F,
    desc: 'Energy use multiple from eating well.',
    src: '02 §4.16; TRN-121',
  }),
  k('4.16', '09', 'habits.travel_rest_jetlag_mult', 0.5, 'multiplier', {
    ...F,
    desc: 'Jet lag multiple from resting after travel.',
    src: '02 §4.16; TRN-131',
  }),
  k('4.16', '09', 'jetlag.consistency_points', 1.0, 'points', {
    ...F,
    type: 'numeric',
    desc: 'Consistency points lost to jet lag.',
    src: '02 §4.16; TRN-131',
  }),
  k('4.16', '09', 'jetlag.composure_start_points', 5, 'points', {
    ...F,
    type: 'numeric',
    desc: 'Composure points at the start lost to jet lag.',
    src: '02 §4.16; TRN-131',
  }),
  k('4.16', '09', 'niggle.ease_off_load_mult', 0.8, 'multiplier', {
    ...F,
    desc: 'Training load multiple when easing off with a niggle.',
    src: '02 §4.16; TRN-201',
  }),
  k('4.16', '37', 'family.away_floor_on', true, 'boolean', {
    ...F,
    desc: "A family junior's away floor is on.",
    src: '02 §4.16; FAM-035',
  }),
  k('4.16', '37', 'family.light_day_max_hours', 2.0, 'hours', {
    ...F,
    type: 'numeric',
    desc: "Most hours on a junior's light day.",
    src: '02 §4.16; FAM-041',
  }),
  k('4.16', '37', 'family.enjoy_fun_plenty_margin', 0.1, 'share', {
    ...F,
    desc: "Margin for a junior's fun to count as plenty.",
    src: '02 §4.16; FAM-041',
  }),
  k('4.16', '13', 'junior_events.final_journeys_free', true, 'boolean', {
    desc: 'Journeys to junior finals (county and national) are free.',
    src: '02 §4.15 and §4.16; CAR-252',
    ...F,
    note: 'It now covers the county final too',
  }),
  k('4.16', '08', 'ai_pop.juniors.uki', 800, 'count', {
    desc: 'Computer-run junior golfers in the UK and Ireland hub.',
    src: '02 §4.16; GOL-140',
  }),
  k('4.16', '08', 'ai_pop.juniors.usa', 600, 'count', {
    desc: 'Computer-run junior golfers in the USA hub.',
    src: '02 §4.16; GOL-140',
  }),
  k('4.16', '14', 'plan.cards_max', 3, 'count', {
    client: true,
    desc: 'Hole plan cards at most.',
    src: '02 §4.16; CMP-310',
    range: [2, 3],
  }),
  k('4.16', '14', 'routine.short_putt_max_m', 1.8288, 'm', {
    client: true,
    desc: 'Longest short putt in the Routine, metres (6 ft exactly).',
    src: '02 §4.16; CMP-350',
    note: '6 ft exactly',
  }),

  // ---------------------------------------------------------------- Keys only their owner's table prints
  // 04 §8.1 (ARC-187): "each is ⚖ for safety, and each lives in the config registry with the
  // others of 02 §4".
  own('04', 'auth.otp_per_hour', 5, 'count', {
    ...F,
    range: [3, 10],
    desc: 'One-time codes and magic links an hour for one email at most.',
    src: '04 §8.1; ARC-187 (each key is ⚖ for safety)',
  }),
  own('04', 'auth.signin_attempts_per_address_hour', 30, 'count', {
    ...F,
    range: [10, 60],
    desc: 'Sign-in attempts an hour from one address at most.',
    src: '04 §8.1; ARC-187 (each key is ⚖ for safety)',
  }),
  own('04', 'auth.otp_max_wrong', 5, 'count', {
    ...F,
    range: [3, 10],
    desc: 'Wrong entries after which a code is void.',
    src: '04 §8.1; ARC-187 (each key is ⚖ for safety)',
  }),
  own('04', 'auth.challenge_after_failures', 3, 'count', {
    ...F,
    range: [1, 10],
    desc: 'Failures from one address after which a challenge is shown.',
    src: '04 §8.1; ARC-187 (each key is ⚖ for safety)',
  }),
  // 05 §4: the keys the Stage 0 database functions read.
  own('05', 'data.soft_deleted_purge_days', 30, 'days', {
    range: [7, 90],
    desc: 'Days after which soft-deleted content is purged.',
    src: '05 §4; DAT-009',
  }),
  own('05', 'data.idempotency_ttl_hours', 24, 'hours', {
    range: [1, 168],
    desc: "Hours a command's idempotency row is kept.",
    src: '05 §4; DAT-040',
  }),
  own('05', 'data.outbox_settle_ms', 2000, 'ms', {
    range: [500, 10000],
    desc: 'Settle delay before outbox consumers read an event.',
    src: '05 §4; DAT-042',
  }),
  own('05', 'data.partition_premake_months', 3, 'months', {
    range: [2, 6],
    desc: 'Months of partitions made ahead.',
    src: '05 §4; DAT-013',
  }),
  own('05', 'jobs.lease_seconds', 60, 'seconds', {
    range: [15, 600],
    desc: 'Lease a worker holds on a claimed job.',
    src: '05 §4; DAT-043',
  }),
  // 26 §4: the keys the approval queue and admin sessions read.
  own('26', 'admin.approval_expiry_days', 7, 'days', {
    range: [3, 30],
    desc: 'Days after which an unapproved change request expires.',
    src: '26 §4; ADM-026',
  }),
  own('26', 'admin.session_timeout_min', 30, 'minutes', {
    range: [15, 120],
    desc: 'Admin session signs out after this many idle minutes.',
    src: '26 §4; ADM-003; ruling H30',
  }),
  own('26', 'admin.session_max_hours', 12, 'hours', {
    range: [4, 12],
    desc: 'Longest any admin session lasts.',
    src: '26 §4; ADM-003; ruling H30',
  }),
  // 20 §4: 02 §4.16 names the key as 20's and prints no value beside it.
  own('20', 'wellbeing.break_reminder_minutes', 90, 'minutes', {
    range: [45, 120],
    client: true,
    desc: 'Minutes of continuous play before the break reminder.',
    src: "20 §4; ENG-198, ENG-232; 02 §4.16 (the key is 20's)",
    note: 'An under-18 account cannot switch the reminder off (ENG-198)',
  }),

  // ---------------------------------------------------------------- Keys that arrive with Stage 2
  // The keys the full shot engine, its named proofs and the round record read (Stage 2), which
  // only their owner's key table prints. SEED-150, version 2: "keys that arrive with a later
  // stage are approved in that stage's batch in the same way", so each loads in the batch
  // `stage2_initial`, and its ⚖ ones wait there for the founder's approval (decision record 0070).
  //
  // 10 §4.3, the pass lines (SIM-004: "these lines decide whether a version may be activated").
  // "None is tunable. The ⚖ ones are fairness lines and change only through the approval queue."
  own('10', 'sim.gate.pressure_top_spread', [1.08, 1.15], 'multiplier', {
    ...S2,
    desc: 'Pressure budget line (c): how much wider both spreads are at the highest pressure, from and to.',
    src: '10 §4.3; SIM-133 (c); ruling G30; 31 TST-185',
    note: 'Fixed (ruling G30): the engine refuses any other pair (10 §4.3)',
  }),
  own('10', 'sim.gate.routine_relief_share', [0.4, 0.55], 'share', {
    ...S2,
    desc: 'Pressure budget line (d): the share of every pressure effect that a full routine with breathing takes away, from and to.',
    src: '10 §4.3; SIM-133 (d); ruling G30; 31 TST-185',
    note: 'Fixed (ruling G30): the engine refuses any other pair (10 §4.3)',
  }),
  own('10', 'sim.gate.detail_mean_strokes', 0.1, 'strokes', {
    ...S2,
    range: [0.05, 0.2],
    desc: 'Most that a scores-only round may differ in mean from a full-detail round, in strokes.',
    src: '10 §4.3; SIM-150',
  }),
  own('10', 'sim.gate.detail_ks_p', 0.01, 'share', {
    ...S2,
    range: [0.001, 0.05],
    desc: 'The two-sample test of scores-only against full-detail rounds must give a p-value above this.',
    src: '10 §4.3; SIM-150',
  }),
  own('10', 'sim.gate.round_trip_shots', 1.0, 'shots', {
    ...S2,
    type: 'numeric',
    range: [0.5, 1.5],
    desc: 'The handicap round trip: how far a band may land from where it started, in shots.',
    src: '10 §4.3; SIM-201',
  }),
  own('10', 'sim.gate.natural_curve_shots', 0.1, 'shots', {
    ...S2,
    range: [0.05, 0.2],
    desc: 'Line of the natural curve check (SIM-202), in shots.',
    src: '10 §4.3; SIM-202',
  }),
  own('10', 'sim.gate.advice_right_points', 3, 'points', {
    ...S2,
    range: [1, 5],
    desc: 'Line of the caddie advice check (SIM-276), in points.',
    src: '10 §4.3; SIM-276',
  }),
  own('10', 'sim.gate.caddie_target_tolerance', 0.2, 'shots', {
    ...S2,
    ...F,
    range: [0.1, 0.3],
    desc: 'Caddie limit line (a): how far each level may be from its target in band 10, in shots.',
    src: '10 §4.3; SIM-279 (a); c27; 31 TST-182',
  }),
  own('10', 'sim.gate.caddie_min_effect_shots', 0.5, 'shots', {
    ...S2,
    ...F,
    range: [0.3, 0.8],
    desc: 'Caddie limit line (b): the least that level 1 to level 5 is worth in every band, in shots.',
    src: '10 §4.3; SIM-279 (b); 31 TST-182',
  }),
  own('10', 'sim.gate.caddie_perfect_player_max', 0.7, 'shots', {
    ...S2,
    ...F,
    range: [0.5, 1.0],
    desc: 'Caddie limit line (d): the most that level 1 to level 5 is worth with the perfect player choosing, in shots.',
    src: '10 §4.3; SIM-279 (d); c27 (0.5 before); 31 TST-182',
  }),
  own('10', 'sim.gate.caddie_min_step_shots', 0.25, 'shots', {
    ...S2,
    ...F,
    range: [0.15, 0.4],
    desc: 'Caddie limit line (c): the least each level must beat the level below by in band 10, in shots.',
    src: '10 §4.3; SIM-279 (c); c27; 31 TST-182',
  }),
  own('10', 'sim.gate.composure_13th_min', 40, 'points', {
    ...S2,
    range: [30, 60],
    desc: 'Line of the composure check on the 13th (SIM-120), in points.',
    src: '10 §4.3; SIM-120; c27',
  }),
  own('10', 'sim.gate.judgement_max_shots', 1.9, 'shots', {
    ...S2,
    range: [1.5, 2.5],
    desc: 'Judgement line: the most that perfect decisions may be worth over the caddie, in shots a round.',
    src: '10 §4.3; SIM-297; ruling K11; 31 TST-180',
  }),
  own('10', 'sim.gate.judgement_l5_max_shots', 0.75, 'shots', {
    ...S2,
    range: [0.5, 1.0],
    desc: 'Judgement line: the most that perfect decisions may be worth over a level 5 caddie, in shots a round.',
    src: '10 §4.3; SIM-297',
  }),
  own('10', 'sim.gate.careless_min_loss_shots', 1.0, 'shots', {
    ...S2,
    type: 'numeric',
    range: [0.5, 1.5],
    desc: 'Judgement line: the least that careless decisions must lose against the caddie, in shots a round.',
    src: '10 §4.3; SIM-297',
  }),
  own('10', 'sim.gate.rebuild_hit_share_e0', 0.01, 'share', {
    ...S2,
    ...F,
    range: [0.01, 0.01],
    desc: 'Truth line: a rebuild of a hidden value "finds" it when it lands within this share of the starting half-width; for the two-band solve, within this share of the wider of the two bands\' own half-widths, the golfer\'s own range (c58; decision record 0165).',
    src: '10 §4.3; SIM-319, test 2; 31 TST-184',
    note: 'Fixed (10 §4.3): the range is the one value',
  }),
  own('10', 'sim.gate.rebuild_max_rate', 0.05, 'share', {
    ...S2,
    ...F,
    range: [0.05, 0.05],
    desc: 'Truth line: the run fails if a rebuild finds the truth on more than this share of the strokes.',
    src: '10 §4.3; SIM-319, test 2; 31 TST-184',
    note: 'Fixed (10 §4.3): the range is the one value',
  }),
  own('10', 'sim.gate.thought_min_share', 0.05, 'share', {
    ...S2,
    range: [0.02, 0.15],
    desc: 'The thought line: the least share of its strokes on which the perfect player must take each of the twelve thoughts.',
    src: '10 §4.3; SIM-297; ruling L2; 26 ADM-095',
  }),
  // 10 §4: the pressure budget's lines that already had keys (SIM-133), and the flag of the
  // option-worth report that the pressure budget's own check reads (31 TST-185).
  own('10', 'sim.pressure.club_round_max_cost', 0.3, 'shots', {
    ...S2,
    range: [0.2, 0.4],
    desc: 'Pressure budget line (a): the most that nerves may cost band 10 in a club round, in shots.',
    src: '10 §4; SIM-133 (a); 31 TST-185',
  }),
  own('10', 'sim.pressure.band_cost_factor', 1.5, 'multiplier', {
    ...S2,
    range: [1.0, 2.0],
    desc: 'Pressure budget line (b): every amateur band may lose at most this many times line (a).',
    src: '10 §4; SIM-133 (b); 31 TST-185',
  }),
  own('10', 'sim.worth.nerves_min_index', 0.25, 'index', {
    ...S2,
    range: [0.1, 0.5],
    desc: 'The option-worth report judges a full routine and breathing on the strokes whose pressure index is at least this.',
    src: '10 §4; SIM-297; 31 TST-185',
  }),
  // 10 §4: the twelve thoughts (fifth rulings, L2). "From the fifth rulings (5 October 2026)
  // the shot.thought.* family … marked ⚖". Design values: changed by hand, after the
  // option-worth report, through the approval queue (26 ADM-095).
  own('10', 'shot.thought.match.mult', 0.97, 'multiplier', {
    ...S2,
    ...F,
    range: [0.94, 0.99],
    desc: 'How much a thought that matches the shot tightens it.',
    src: '10 §4; SIM-251; ruling L2',
  }),
  own('10', 'shot.thought.pressure_min', 0.5, 'index', {
    ...S2,
    ...F,
    range: [0.2, 0.7],
    desc: 'The two thoughts for pressure act, and are judged, only above this pressure index.',
    src: '10 §4; SIM-251, SIM-297; ruling L2',
  }),
  own('10', 'shot.thought.smooth_tempo.mishit_relief', 0.25, 'share', {
    ...S2,
    ...F,
    range: [0.1, 0.4],
    desc: 'Smooth tempo: the share of the named mishit chance it takes away.',
    src: '10 §4; SIM-251; ruling L2',
  }),
  own('10', 'shot.thought.stay_tall.mishit_relief', 0.25, 'share', {
    ...S2,
    ...F,
    range: [0.1, 0.4],
    desc: 'Stay tall: the share of the named mishit chance it takes away.',
    src: '10 §4; SIM-251; ruling L2',
  }),
  own('10', 'shot.thought.brush_the_grass.mishit_relief', 0.25, 'share', {
    ...S2,
    ...F,
    range: [0.1, 0.4],
    desc: 'Brush the grass: the share of the named mishit chance it takes away.',
    src: '10 §4; SIM-251; ruling L2',
  }),
  own('10', 'shot.thought.keep_turning.nerves_mishit_relief', 0.25, 'share', {
    ...S2,
    ...F,
    range: [0.1, 0.4],
    desc: 'Keep turning: the share of the mishit chance from nerves it takes away.',
    src: '10 §4; SIM-251; ruling L2',
  }),
  own('10', 'shot.thought.finish_the_swing.dist_spread', 0.97, 'multiplier', {
    ...S2,
    ...F,
    range: [0.94, 0.99],
    desc: 'Finish the swing: how much it tightens the distance spread.',
    src: '10 §4; SIM-251; ruling L2',
  }),
  own('10', 'shot.thought.small_target.line_spread', 0.97, 'multiplier', {
    ...S2,
    ...F,
    range: [0.94, 0.99],
    desc: 'Small target: how much it tightens the line spread.',
    src: '10 §4; SIM-251; ruling L2',
  }),
  own('10', 'shot.thought.small_target.dist_spread', 1.02, 'multiplier', {
    ...S2,
    ...F,
    range: [1.0, 1.05],
    desc: 'Small target: how much it widens the distance spread.',
    src: '10 §4; SIM-251; ruling L2',
  }),
  own('10', 'shot.thought.soft_hands.proximity', 0.96, 'multiplier', {
    ...S2,
    ...F,
    range: [0.92, 0.99],
    desc: 'Soft hands: how much nearer the short-game shot finishes.',
    src: '10 §4; SIM-251; ruling L2',
  }),
  own('10', 'shot.thought.land_on_a_spot.proximity', 0.96, 'multiplier', {
    ...S2,
    ...F,
    range: [0.92, 0.99],
    desc: 'Land on a spot: how much nearer the short-game shot finishes.',
    src: '10 §4; SIM-251; ruling L2',
  }),
  own('10', 'shot.thought.head_still.line_spread', 0.95, 'multiplier', {
    ...S2,
    ...F,
    range: [0.9, 0.99],
    desc: 'Head still: how much it tightens the line of a putt.',
    src: '10 §4; SIM-251; ruling L2',
  }),
  own('10', 'shot.thought.smooth_stroke.dist_spread', 0.95, 'multiplier', {
    ...S2,
    ...F,
    range: [0.9, 0.99],
    desc: 'Smooth stroke: how much it tightens the pace of a putt.',
    src: '10 §4; SIM-251; ruling L2',
  }),
  own('10', 'shot.thought.roll_it_over_a_spot.line_spread', 0.96, 'multiplier', {
    ...S2,
    ...F,
    range: [0.92, 0.99],
    desc: 'Roll it over a spot: how much it tightens the line of a breaking putt.',
    src: '10 §4; SIM-251; ruling L2',
  }),
  own('10', 'shot.thought.listen_for_it.nerves', 0.9, 'multiplier', {
    ...S2,
    ...F,
    range: [0.8, 0.97],
    desc: 'Listen for it: how much it softens nerves on a putt.',
    src: '10 §4; SIM-251; ruling L2',
  }),
  own('10', 'shot.thought.finish_the_swing.headwind_min_mps', 2.2, 'm_per_s', {
    ...S2,
    ...F,
    range: [1, 5],
    desc: 'Finish the swing acts from this head wind (5 mph).',
    src: '10 §4; SIM-251; ruling L2',
  }),
  own('10', 'shot.thought.stay_tall.slope_min_deg', 2.0, 'deg', {
    ...S2,
    ...F,
    type: 'numeric',
    range: [1, 5],
    desc: 'Stay tall acts from this tilt of the lie, in degrees.',
    src: '10 §4; SIM-251; ruling L2',
  }),
  own('10', 'shot.thought.roll_it_over_a_spot.break_min_m', 0.108, 'm', {
    ...S2,
    ...F,
    range: [0.05, 0.25],
    desc: 'Roll it over a spot acts from this much break (4.25 in: the width of the hole).',
    src: '10 §4; SIM-251; ruling L2',
  }),
  // 08 §4: a line that already had its key (10 §4.3: SIM-202).
  own('08', 'adaptive.calibration_tolerance_shots', 0.3, 'shots', {
    ...S2,
    range: [0.1, 0.5],
    desc: 'How far a golfer with an adaptive profile may score from a same-level golfer without one, in shots.',
    src: '08 §4; GOL-152; 10 SIM-202',
  }),
  // 31 §4: the sizes and the line of the truth tests (TST-184).
  own('31', 'test.h6_scan_rounds', 1000, 'rounds', {
    ...S2,
    range: [200, 5000],
    desc: 'Scripted rounds in the payload scan of the truth tests.',
    src: '31 §4; TST-184',
  }),
  own('31', 'test.h6_bot_rounds', 20000, 'rounds', {
    ...S2,
    range: [5000, 50000],
    desc: 'Paired rounds each bot plays in the truth tests.',
    src: '31 §4; TST-184',
  }),
  own('31', 'test.h6_bot_max_gain_shots', 0.05, 'shots', {
    ...S2,
    ...F,
    range: [0.02, 0.1],
    desc: 'The most that a bot using the public facts may gain, in shots a round.',
    src: '31 §4; TST-184; ruling H6',
  }),
  // 10 §4: the five ⚖ keys of §3.35, the sizes of the hidden parts (SIM-311, SIM-312, SIM-314), at
  // 10 §4's own defaults and ranges (decision record 0217). Besides its range, a change to one of them
  // must keep the sums of SIM-319 test 6 (31 TST-184 test 8): ops.config_h6_sums_check refuses it when
  // it is proposed and again before it is approved (decision record 0216).
  own('10', 'sim.wind.local_sd_share', 0.5, 'share', {
    ...S2,
    ...F,
    range: [0.41, 0.57],
    desc: 'The SD of the local variation of the wind, as a share of the starting half-width of the wind bands (speed and direction).',
    src: '10 §4; SIM-311; SIM-319, test 6; 31 TST-184, test 8',
    note: '1/√6 to 1/√3; the sums check holds it there exactly',
  }),
  own('10', 'sim.dist.local_sd_share', 0.5, 'share', {
    ...S2,
    ...F,
    range: [0.41, 0.57],
    desc: 'The SD of the seeded part of the plays-like distance, as a share of the starting half-width of the distance band.',
    src: '10 §4; SIM-312; SIM-319, test 6; 31 TST-184, test 8',
    note: '1/√6 to 1/√3; the sums check holds it there exactly',
  }),
  own('10', 'sim.slope.local_sd_share', 0.5, 'share', {
    ...S2,
    ...F,
    range: [0.41, 0.57],
    desc: 'The SD of the seeded part of the stance, as a share of the starting half-width of the stance band.',
    src: '10 §4; SIM-312; SIM-319, test 6; 31 TST-184, test 8',
    note: '1/√6 to 1/√3; the sums check holds it there exactly',
  }),
  own('10', 'sim.green.daily_character_sd', 0.28, 'share', {
    ...S2,
    ...F,
    range: [0.15, 0.4],
    desc: "The SD of the day's character of a green, as a share of the starting half-width of the break and pace bands.",
    src: '10 §4; SIM-314; SIM-319, test 6; 31 TST-184, test 8',
    note: 'With the spot: (day + spot) × √3 ≤ 1 and √(day² + spot²) ≥ 0.75/√6 (0.306)',
  }),
  own('10', 'sim.green.spot_sd_share', 0.28, 'share', {
    ...S2,
    ...F,
    range: [0.15, 0.4],
    desc: 'The SD of the spot under a putt, as a share of the starting half-width of the break and pace bands.',
    src: '10 §4; SIM-314; SIM-319, test 6; 31 TST-184, test 8',
    note: "With the day's character: (day + spot) × √3 ≤ 1 and √(day² + spot²) ≥ 0.75/√6 (0.306)",
  }),
  // 05, 38 and 14 §4: the keys the round record reads (H5's report, finding 8).
  own('05', 'data.shots_retention_seasons', 2, 'seasons', {
    ...S2,
    range: [1, 6],
    desc: 'Seasons a stroke row is kept (182 days).',
    src: '05 §4; DAT-080, DAT-110; 38 VW-019',
  }),
  own('38', 'recording.ai_cache_hours', 24, 'hours', {
    ...S2,
    range: [1, 168],
    desc: "Hours a recording's AI text is kept in the cache.",
    src: '38 §4.2; VW-020',
  }),
  own('14', 'caddie_style.highlights_seconds', 150, 'seconds', {
    ...S2,
    range: [60, 240],
    desc: 'Length of the highlights of a round the caddie played, in seconds.',
    src: '14 §4; CMP-302',
  }),
  // 29 §4.4: the database-size trigger that `jobs.scaling_watch` reads from Stage 2 (OPS-090; OPS-T16).
  // It arrives with Stage 2, so it loads in `stage2_initial` too; it carries no ⚖, so it is live at once.
  own('29', 'ops.scale_trigger.db_size_pct', 80, 'percent', {
    ...S2,
    range: [60, 90],
    desc: 'Database size, as a share of the disk the plan includes, that opens an admin task.',
    src: '29 §4.4; OPS-090, OPS-T16',
  }),
  // 39 §3: the habit loop's keys (decision record 0109). They arrive with Stage 2, when the
  // habit-loop branch joins it, so they load in `stage2_initial`; none carries ⚖. The package that
  // reads them, `@fairway/habit`, is behind the flag `habit_loop`, which is off.
  own('39', 'habit.active_suppress_minutes', 10, 'minutes', {
    ...S2,
    range: [0, 60],
    desc: 'Nothing of the habit loop is pushed while the player is in the app, or was in it within this many minutes.',
    src: '39 §3; HAB-018',
  }),
  own('39', 'habit.guest_keep_days', 14, 'days', {
    ...S2,
    range: [7, 28],
    desc: "Days a Challenge a friend guest's match is kept for them if they make an account.",
    src: '39 §3; HAB-012',
  }),
  own('39', 'habit.since_lines_max', 5, 'count', {
    ...S2,
    range: [3, 5],
    desc: 'The most lines in "Since you last looked" on Today.',
    src: '39 §3; HAB-004',
  }),
  own('39', 'habit.society_benchmark_below', 3, 'rounds', {
    ...S2,
    range: [0, 5],
    desc: "Below this many counting rounds, a society's week shows an AI benchmark row.",
    src: '39 §3; HAB-014',
  }),
  own('39', 'habit.return_decisions_max', 2, 'count', {
    ...S2,
    range: [1, 2],
    desc: 'The most decisions offered to a player coming back.',
    src: '39 §3; HAB-016',
  }),
  // ---------------------------------------------------------------- Keys that arrive with Stage 3
  // The AI gateway's keys (21 §4.6; 40 §4) and the moderation lines that the name check and the
  // classifier read (22 §4). The gateway runs on the fake provider only: no paid route is wired,
  // and `ai.grant.recorded` stays false until the founder records the grant (40 AIE-034).
  own('21', 'ai.kill_switch_all', false, 'boolean', {
    ...S3,
    desc: 'Every AI task falls back to its template or rules text.',
    src: '21 §4.6; AI-006',
  }),
  own('21', 'ai.budget.daily_cap_gbp', 6.0, 'gbp', {
    ...S3,
    ...F,
    type: 'numeric',
    range: [0, 500],
    desc: 'The most the game spends on AI in one Europe/London day, in pounds.',
    src: '21 §4.6; AI-090; D-11',
  }),
  own('21', 'ai.budget.monthly_cap_gbp', 150, 'gbp', {
    ...S3,
    ...F,
    type: 'numeric',
    range: [0, 10000],
    desc: 'The most the game spends on AI in one month, in pounds.',
    src: '21 §4.6; AI-092',
  }),
  own('21', 'ai.budget.safety_reserve_gbp', 1.0, 'gbp', {
    ...S3,
    ...F,
    type: 'numeric',
    range: [0, 50],
    desc: 'Pounds kept for the safety tasks after the daily cap is reached.',
    src: '21 §4.6; AI-091',
  }),
  own('21', 'ai.budget.offline_monthly_cap_gbp', 50, 'gbp', {
    ...S3,
    ...F,
    type: 'numeric',
    range: [0, 2000],
    desc: 'The most the build-time AI pipelines spend in one month, in pounds.',
    src: '21 §4.6',
  }),
  own('21', 'ai.budget.degrade_thresholds', [0.7, 0.85, 1.0], 'share', {
    ...S3,
    type: 'json',
    desc: 'The shares of the daily cap at which AI use steps down (21 §3.9).',
    src: '21 §4.6; §3.9',
  }),
  own('21', 'ai.fx.usd_gbp', 0.7547, 'multiplier', {
    ...S3,
    range: [0.5, 1.0],
    desc: 'Pounds to one US dollar, for costing AI calls. Checked monthly.',
    src: '21 §4.6; AI-160',
  }),
  own('21', 'ai.factcheck.regen_attempts', 1, 'count', {
    ...S3,
    range: [0, 2],
    desc: 'How many times an output that fails its fact check is written again.',
    src: '21 §4.6; AI-140',
  }),
  own(
    '21',
    'ai.factcheck.idiom_allowlist',
    ['one of', 'no one', 'one another', 'at first'],
    'structure',
    {
      ...S3,
      desc: 'Phrases with a number word that the fact check lets through.',
      src: '21 §4.6; C4',
    },
  ),
  own('21', 'ai.moderation.output_block_threshold', 0.3, 'share', {
    ...S3,
    range: [0.05, 0.6],
    desc: 'An AI output scoring this or more in any harm category is not published.',
    src: '21 §4.6; AI-130',
  }),
  own('21', 'ai.bio.max_regenerations_per_week', 2, 'count', {
    ...S3,
    range: [0, 5],
    desc: 'How many biography paragraphs an owner may have written again in a week.',
    src: '21 §4.6; AI-097, AI-321',
  }),
  own('40', 'ai.jobs.max_attempts', 2, 'count', {
    ...S3,
    range: [1, 3],
    desc: 'Billable calls one AI job may make, repairs and retries included.',
    src: '40 §4; AIE-011',
  }),
  own('40', 'ai.jobs.deadline_seconds', 30, 'seconds', {
    ...S3,
    range: [5, 120],
    desc: 'How long an AI job may run before it expires.',
    src: '40 §4; AIE-012',
  }),
  own('40', 'ai.jobs.max_input_tokens', 8000, 'count', {
    ...S3,
    range: [1000, 100000],
    desc: 'The largest input one AI job may send, in tokens.',
    src: '40 §4; AIE-030',
  }),
  own('40', 'ai.jobs.max_output_tokens', 800, 'count', {
    ...S3,
    range: [100, 6000],
    desc: "The largest output one AI job may ask for, in tokens; a task's own lower limit wins.",
    src: '40 §4; AIE-030',
  }),
  own('40', 'ai.jobs.max_cost_per_job_usd', 0.02, 'usd', {
    ...S3,
    range: [0.001, 0.5],
    desc: 'The most one narrative AI job may cost, in US dollars.',
    src: '40 §4; AIE-030',
  }),
  own('40', 'ai.grant.recorded', false, 'boolean', {
    ...S3,
    desc: 'The founder has recorded the API grant in the cost log. Until then no paid AI call is made.',
    src: '40 §4; AIE-034',
  }),
  own('40', 'ai.grant.working_ceiling_usd', 180, 'usd', {
    ...S3,
    ...F,
    type: 'numeric',
    range: [0, 200],
    desc: "The most of the founder's monthly API grant that may be planned, in US dollars.",
    src: '40 §4; AIE-036',
  }),
  own('40', 'ai.grant.reserve_usd', 20, 'usd', {
    ...S3,
    ...F,
    type: 'numeric',
    range: [20, 200],
    desc: 'The part of the API grant that is never planned, in US dollars.',
    src: '40 §4; AIE-036',
  }),
  own('40', 'ai.pilot.max_total_usd', 5.0, 'usd', {
    ...S3,
    ...F,
    type: 'numeric',
    range: [0, 5],
    desc: 'The most the first paid AI experiment may spend in all, in US dollars.',
    src: '40 §4; AIE-091',
  }),
  own('40', 'ai.ledger.alert_pcts', [50, 75, 90], 'percent', {
    ...S3,
    desc: 'The shares of the working allowance at which the founder is alerted.',
    src: '40 §4; AIE-033',
  }),
  own('40', 'ai.ledger.max_reconcile_age_minutes', 60, 'minutes', {
    ...S3,
    range: [15, 1440],
    desc: 'Paid AI stops when the last reconciliation is older than this.',
    src: '40 §4; AIE-033',
  }),
  own('40', 'ai.ledger.variance_alert_pct', 5, 'percent', {
    ...S3,
    range: [1, 20],
    desc: 'Paid AI stops when a cost differs from its estimate by more than this share.',
    src: '40 §4; AIE-033',
  }),
  own('22', 'moderation.hold_threshold_u18_channel', 0.35, 'share', {
    ...S3,
    ...F,
    range: [0.2, 0.5],
    desc: 'Text scoring this or more is held for review where an under-18 could see it.',
    src: '22 §4; §3.11',
  }),
  own('22', 'moderation.hold_threshold_adult_channel', 0.55, 'share', {
    ...S3,
    ...F,
    range: [0.4, 0.7],
    desc: 'Text scoring this or more is flagged for review in an adult-only channel.',
    src: '22 §4; §3.11',
  }),
  own('22', 'moderation.block_threshold', 0.85, 'share', {
    ...S3,
    ...F,
    range: [0.7, 0.95],
    desc: 'Text scoring this or more is blocked in every channel.',
    src: '22 §4; §3.11',
  }),
  // Stage 4: the keys of 09 §4.1 that the training code, the daily job and the prices read (SEED-150,
  // "keys that arrive with a later stage"). Their values equal packages/training's defaultConfig
  // (tools/seed/test/config-stage4.test.ts). The travel keys are 07's and arrive with Stage 5; the
  // membership keys are 18's and arrive with Stage 9; the age anchors stay in §3.4's table and the
  // package. The prices' ranges are TRN-193's table's (decision record 0256).
  own('09', 'training.day_start_local', '09:00', 'time_of_day', {
    ...S4,
    desc: "The time a golfer's training blocks start, local time; juniors' school days start at 16:00.",
    src: '09 §4.1; TRN-003',
  }),
  own('09', 'training.base_rate_at_52', 0.198, 'points', {
    ...S4,
    ...F,
    range: [0.1, 0.35],
    desc: 'Skill points an effective hour gives a golfer at level 52, before every multiplier.',
    src: '09 §4.1; TRN-030',
  }),
  own('09', 'training.level_decay', 150, 'points', {
    ...S4,
    ...F,
    range: [60, 600],
    desc: 'How fast gains shrink as a skill rises: the level gap that halves the rate.',
    src: '09 §4.1; TRN-030',
  }),
  own('09', 'training.headroom_full_points', 8, 'points', {
    ...S4,
    ...F,
    range: [4, 15],
    desc: 'Points below the limit at which a skill still gains at full rate.',
    src: '09 §4.1; TRN-030',
  }),
  own('09', 'training.over_cap_efficiency', 0.5, 'share', {
    ...S4,
    ...F,
    range: [0.25, 0.75],
    desc: 'What an hour of practice above the 4-hour soft cap is worth.',
    src: '09 §4.1; TRN-032',
  }),
  own('09', 'training.env_bad_weather', 0.75, 'multiplier', {
    ...S4,
    range: [0.5, 1.0],
    desc: 'What an hour outdoors in bad weather is worth.',
    src: '09 §4.1; TRN-030',
  }),
  own('09', 'training.env_simulator', 0.85, 'multiplier', {
    ...S4,
    range: [0.5, 1.0],
    desc: 'What an hour in a simulator is worth.',
    src: '09 §4.1; TRN-030',
  }),
  own('09', 'training.env_home', 0.8, 'multiplier', {
    ...S4,
    range: [0.5, 1.0],
    desc: 'What an hour of mental or study work at home is worth.',
    src: '09 §4.1; TRN-030',
  }),
  own('09', 'training.bad_weather_transfer', 0.3, 'share', {
    ...S4,
    range: [0, 0.5],
    desc: 'The share of a bad-weather hour that also trains the weather skill.',
    src: '09 §4.1; TRN-030',
  }),
  own('09', 'training.enjoyment_mult_min', 0.5, 'multiplier', {
    ...S4,
    ...F,
    range: [0.25, 1.0],
    desc: "The lowest a family junior's enjoyment multiplier can go (1.00 until Stage 13).",
    src: '09 §4.1; TRN-033',
  }),
  own('09', 'training.facility_boost_per_level', 0.02, 'multiplier', {
    ...S4,
    ...F,
    range: [0.01, 0.03],
    desc: 'Boost per level of the facility an activity uses.',
    src: '09 §4.1; TRN-044',
  }),
  own('09', 'training.club_quality_boost_per_star', 0.01, 'multiplier', {
    ...S4,
    ...F,
    range: [0, 0.02],
    desc: 'Boost per star of the club where the golfer trains.',
    src: '09 §4.1; TRN-044',
  }),
  own('09', 'training.coach_boost_per_level', 0.02, 'multiplier', {
    ...S4,
    ...F,
    range: [0.01, 0.05],
    desc: 'Boost per coach level on every skill.',
    src: '09 §4.1; TRN-045',
  }),
  own('09', 'training.coach_boost_speciality_per_level', 0.04, 'multiplier', {
    ...S4,
    ...F,
    range: [0.01, 0.05],
    desc: "Boost per coach level on the coach's specialities.",
    src: '09 §4.1; TRN-045',
  }),
  own('09', 'training.aid_cap_per_skill', 0.1, 'multiplier', {
    ...S4,
    ...F,
    range: [0.05, 0.15],
    desc: 'Most that training aids together add to one skill.',
    src: '09 §4.1; TRN-046',
  }),
  own('09', 'training.max_aids_equipped', 4, 'count', {
    ...S4,
    range: [2, 6],
    desc: 'Training aids a golfer can have in use at once.',
    src: '09 §4.1; TRN-046',
  }),
  own('09', 'training.lesson_boost', 0.5, 'multiplier', {
    ...S4,
    ...F,
    range: [0.2, 0.8],
    desc: 'Extra a lesson hour gives over practice.',
    src: '09 §4.1; TRN-020',
  }),
  own('09', 'training.lessons_max_per_day', 2, 'count', {
    ...S4,
    ...F,
    range: [1, 2],
    desc: 'Lessons a golfer can take in a day.',
    src: '09 §4.1; TRN-020',
  }),
  own('09', 'training.boost_knee', 0.5, 'multiplier', {
    ...S4,
    ...F,
    range: [0.3, 0.8],
    desc: 'Above this summed boost, each further boost counts at half.',
    src: '09 §4.1; TRN-041',
  }),
  own('09', 'training.support_cap_per_skill', 0.15, 'multiplier', {
    ...S4,
    ...F,
    range: [0.05, 0.2],
    desc: 'Most that personal support together adds to one skill.',
    src: '09 §4.1; TRN-047',
  }),
  own('09', 'training.camp_bonus_max', 0.15, 'multiplier', {
    ...S4,
    ...F,
    range: [0, 0.2],
    desc: "A training camp's largest bonus.",
    src: '09 §4.1; TRN-048',
  }),
  own('09', 'training.camp_full_days', 5, 'days', {
    ...S4,
    ...F,
    range: [3, 7],
    desc: 'Days at a camp that earn its full bonus.',
    src: '09 §4.1; TRN-048',
  }),
  own('09', 'training.camp_bonus_days', 28, 'days', {
    ...S4,
    ...F,
    range: [14, 42],
    desc: "How long a camp's bonus lasts.",
    src: '09 §4.1; TRN-048',
  }),
  own('09', 'training.heuristic_version', 'trn-061/3', 'option', {
    ...S4,
    desc: "The label of the coach's plan rules; every plan carries it.",
    src: '09 §4.1; TRN-060, TRN-061',
    note: 'A label, not a number: pinned by admin, new for every change to TRN-061',
  }),
  own('09', 'training.variety_penalty', 0.8, 'multiplier', {
    ...S4,
    range: [0.6, 1.0],
    desc: "What the same activity is worth after its second block in a day, in the coach's plan.",
    src: '09 §4.1; TRN-061',
  }),
  own('09', 'training.advice_days', 3, 'days', {
    ...S4,
    range: [1, 5],
    desc: 'Days a piece of coach or caddie advice stays in the plan.',
    src: '09 §4.1; TRN-063',
  }),
  own('09', 'form.ewma_alpha', 0.25, 'share', {
    ...S4,
    range: [0.1, 0.5],
    desc: 'How much the latest round moves form.',
    src: '09 §4.1; TRN-080',
  }),
  own('09', 'form.idle_decay', 0.97, 'multiplier', {
    ...S4,
    range: [0.9, 1.0],
    desc: 'How form drifts back each day with no round.',
    src: '09 §4.1; TRN-080',
  }),
  own('09', 'physique.detrain_rate', 0.005, 'share', {
    ...S4,
    range: [0, 0.02],
    desc: 'Share of the gain above the base physique lost each day without gym work.',
    src: '09 §4.1; TRN-091',
  }),
  own('09', 'workload.acute_lambda', 0.25, 'share', {
    ...S4,
    range: [0.15, 0.4],
    desc: 'How fast the 7-day workload average follows each day.',
    src: '09 §4.1; TRN-100',
  }),
  own('09', 'workload.chronic_lambda', 0.069, 'share', {
    ...S4,
    range: [0.04, 0.1],
    desc: 'How fast the 28-day workload average follows each day (2 ÷ 29).',
    src: '09 §4.1; TRN-100',
  }),
  own('09', 'workload.zones', { fresh_below: 0.8, amber_above: 1.3, red_above: 1.5 }, 'structure', {
    ...S4,
    ...F,
    desc: 'The workload ratio lines between fresh, normal, amber and red.',
    src: '09 §4.1; TRN-100',
  }),
  own('09', 'workload.extra_hours_load_surcharge', 0.5, 'share', {
    ...S4,
    ...F,
    range: [0, 1.0],
    desc: 'Extra workload of a bought hour over a plan hour.',
    src: '09 §4.1; TRN-184',
  }),
  own('09', 'injury.enabled', true, 'boolean', {
    ...S4,
    ...F,
    desc: 'The kill switch for injuries.',
    src: '09 §4.1; TRN-101',
  }),
  own('09', 'injury.p_amber', 0.004, 'share', {
    ...S4,
    ...F,
    range: [0.002, 0.006],
    desc: 'Chance of an injury on an amber workload day.',
    src: '09 §4.1; TRN-101',
  }),
  own('09', 'injury.p_red_base', 0.02, 'share', {
    ...S4,
    ...F,
    range: [0.01, 0.03],
    desc: 'Chance of an injury on a red workload day, at the red line.',
    src: '09 §4.1; TRN-101',
  }),
  own('09', 'injury.p_red_slope', 0.08, 'share', {
    ...S4,
    ...F,
    range: [0.04, 0.12],
    desc: 'Extra chance of injury for each 0.1 of the ratio above the red line, per unit.',
    src: '09 §4.1; TRN-101',
  }),
  own('09', 'injury.p_max', 0.06, 'share', {
    ...S4,
    ...F,
    range: [0.03, 0.09],
    desc: 'The highest chance of an injury on any day.',
    src: '09 §4.1; TRN-101',
  }),
  own('09', 'injury.p_extra_hours_per_hour', 0.004, 'share', {
    ...S4,
    ...F,
    range: [0, 0.01],
    desc: 'Extra chance of injury for each bought hour above the plan.',
    src: '09 §4.1; TRN-184',
  }),
  own('09', 'rebuild.fail_lock_days', 42, 'days', {
    ...S4,
    ...F,
    range: [28, 91],
    desc: 'Days before a failed swing rebuild of the same group can start again.',
    src: '09 §4.1; TRN-110',
  }),
  own('09', 'training.extra_hours_min_quality', 0.5, 'share', {
    ...S4,
    ...F,
    range: [0.25, 1.0],
    desc: 'The least plan quality a day needs before bought hours count in full.',
    src: '09 §4.1; TRN-182',
  }),
  own('09', 'training.keepup_gym_hours', 3.0, 'hours', {
    ...S4,
    ...F,
    range: [2.0, 5.0],
    desc: 'Gym hours a week a golfer needs to keep up while using bought hours.',
    src: '09 §4.1; TRN-182',
  }),
  own('09', 'potential.limit_lift_coach_level_min', 9, 'level', {
    ...S4,
    ...F,
    range: [8, 10],
    desc: 'The least coach level that earns the limit lift.',
    src: '09 §4.1; TRN-186',
  }),
  own('09', 'potential.limit_lift_min_weekly_hours', 2.0, 'hours', {
    ...S4,
    ...F,
    range: [1.0, 4.0],
    desc: 'Weekly hours with that coach that earn the limit lift.',
    src: '09 §4.1; TRN-186',
  }),
  own('09', 'training.golf_for_life_hours_per_week', 3, 'hours', {
    ...S4,
    ...F,
    range: [1, 8],
    type: 'numeric',
    desc: 'Golf hours a week, on at least three days, that earn the golf-for-life credit.',
    src: '09 §4.1; TRN-217 (I-008)',
  }),
  own('09', 'training.golf_for_life_credit_per_week', 0.08, 'years', {
    ...S4,
    ...F,
    range: [0, 0.2],
    desc: 'Years of age credit a week of golf for life earns.',
    src: '09 §4.1; TRN-217 (I-008)',
  }),
  own('09', 'membership.tier_separation_min', 0.1, 'share', {
    ...S4,
    ...F,
    range: [0.05, 0.2],
    desc: 'How much each tier must beat the one below on the ladder proof.',
    src: '09 §4.1; TRN-216 (c51)',
  }),
  own('09', 'prices.coach_week_coins_per_level', 120, 'coins', {
    ...S4,
    ...F,
    desc: "A coach's week with two lessons, in Coins for each coach level.",
    src: '09 §4.1; TRN-193',
  }),
  own('09', 'prices.coach_lesson_coins_per_level', 40, 'coins', {
    ...S4,
    ...F,
    desc: 'An extra lesson, in Coins for each coach level.',
    src: '09 §4.1; TRN-193',
  }),
  own('09', 'prices.physio_session_coins', 150, 'coins', {
    ...S4,
    ...F,
    desc: 'A physio visit, in Coins.',
    src: '09 §4.1; TRN-193',
  }),
  own('09', 'prices.mind_coach_week_coins', 2000, 'coins', {
    ...S4,
    ...F,
    desc: "A mind coach's week, in Coins.",
    src: '09 §4.1; TRN-193',
  }),
  own('09', 'prices.nutrition_plan_week_coins', 1000, 'coins', {
    ...S4,
    ...F,
    desc: "A week's nutrition plan, in Coins.",
    src: '09 §4.1; TRN-193',
  }),
  own('09', 'prices.sleep_kit_coins', 3000, 'coins', {
    ...S4,
    ...F,
    desc: 'The sleep-and-travel recovery kit, kept, in Coins.',
    src: '09 §4.1; TRN-193',
  }),
  own('09', 'prices.extra_hour_coins', 200, 'coins', {
    ...S4,
    ...F,
    desc: 'An extra golf hour, in Coins.',
    src: '09 §4.1; TRN-193',
  }),
  own('09', 'prices.camp_week_coins', 6000, 'coins', {
    ...S4,
    ...F,
    desc: 'A week at a training camp, in Coins.',
    src: '09 §4.1; TRN-193',
  }),
  own('08', 'ageing.golf_for_life_max_years', 5, 'years', {
    ...S4,
    ...F,
    range: [0, 10],
    desc: 'The most age credit golf for life can hold: at best a 60-year-old keeps the power and fitness ceilings of a 55-year-old.',
    src: '08 §4; GOL-183 (I-008)',
  }),
  own('08', 'ageing.golf_for_life_decay_per_season', 0.5, 'years', {
    ...S4,
    range: [0, 2],
    desc: 'Age credit lost in a season with fewer than half of the golf-for-life weeks kept.',
    src: '08 §4; GOL-183 (I-008)',
  }),
  own('08', 'study.bought_knowledge_cap', 0.1, 'share', {
    ...S4,
    ...F,
    desc: 'The course knowledge a study block in bought hours may reach in the coach plan (it gives none).',
    src: '08 GOL-180; 09 TRN-061 (c27)',
  }),
  own(
    '09',
    'school.term_calendar.uki',
    [
      { start: '2026-09-02', end: '2026-10-23' },
      { start: '2026-11-02', end: '2026-12-18' },
      { start: '2027-01-04', end: '2027-02-12' },
      { start: '2027-02-22', end: '2027-03-25' },
      { start: '2027-04-12', end: '2027-05-28' },
      { start: '2027-06-07', end: '2027-07-21' },
    ],
    'structure',
    {
      ...S4,
      desc: "England's school terms, half terms left out; loaded by admin each year.",
      src: '09 TRN-010 (VERIFY AT BUILD each year)',
      note: 'Decision record 0256: a common English council pattern for 2026-27',
    },
  ),
  own(
    '09',
    'school.public_holidays.uki',
    [
      '2026-08-31',
      '2026-12-25',
      '2026-12-28',
      '2027-01-01',
      '2027-03-26',
      '2027-03-29',
      '2027-05-03',
      '2027-05-31',
      '2027-08-30',
    ],
    'structure',
    {
      ...S4,
      desc: "England's bank holidays: never a school day.",
      src: '09 TRN-010 (VERIFY AT BUILD each year)',
    },
  ),
  own(
    '09',
    'school.term_calendar.usa',
    [
      { start: '2026-08-24', end: '2026-11-24' },
      { start: '2026-11-30', end: '2026-12-18' },
      { start: '2027-01-04', end: '2027-03-12' },
      { start: '2027-03-22', end: '2027-06-04' },
    ],
    'structure',
    {
      ...S4,
      desc: 'A typical US district school year, late August to early June; loaded by admin each year.',
      src: '09 TRN-010 (VERIFY AT BUILD each year)',
    },
  ),
  own(
    '09',
    'school.public_holidays.usa',
    [
      '2026-09-07',
      '2026-11-26',
      '2026-12-25',
      '2027-01-01',
      '2027-01-18',
      '2027-02-15',
      '2027-05-31',
    ],
    'structure',
    {
      ...S4,
      desc: 'US federal holidays inside the school year: never a school day.',
      src: '09 TRN-010 (VERIFY AT BUILD each year)',
    },
  ),

  // Stage 5: the keys of 06 §4.7 that the season clock and the calendar generator read (CLK-143).
  // Their values equal packages/clock's defaultConfig (tools/seed/test/config-stage5.test.ts). Every
  // calendar.* key, and time.turn_day, is ⚖ (06 §4.7, the sentence after its first table; §8). The two
  // keys 02 §4.1 prints (time.season_length_days, calendar.major_default_start_days) keep 02's mark, which
  // Stage 0's approved batch holds (decision record 0284). The daylight, weather, turf, closure and winter
  // keys arrive with the steps that read them.
  own('06', 'time.turn_day', 43, 'days', {
    ...S5,
    ...F,
    client: true,
    fixed: true,
    range: [43, 43],
    desc: 'The day of the season on which The Turn falls: the start of day 43, half-way. FIXED.',
    src: '06 §4.7; CLK-124',
  }),
  own(
    '06',
    'time.season1_anchor_dates',
    [
      { month: 'march', day: 1 },
      { month: 'june', day: 1 },
      { month: 'september', day: 1 },
      { month: 'december', day: 1 },
    ],
    'structure',
    {
      ...S5,
      fixed: true,
      desc: "The dates main Season 1 may start nearest to: the Monday nearest each is allowed without the founder's approval.",
      src: '06 §4.7; CLK-003',
    },
  ),
  own('06', 'calendar.major_latest_end_day', 84, 'days', {
    ...S5,
    ...F,
    range: [77, 84],
    desc: 'The last season day on which a major may end.',
    src: '06 §4.7; CLK-012',
  }),
  own('06', 'calendar.major_min_gap_days', 3, 'days', {
    ...S5,
    ...F,
    range: [2, 7],
    desc: 'The fewest clear days between two majors.',
    src: '06 §4.7; CLK-012',
  }),
  own(
    '06',
    'calendar.regular_event_weekdays',
    [
      [2, 3],
      [6, 7],
    ],
    'structure',
    {
      ...S5,
      ...F,
      desc: 'The two 2-day windows of a week that regular events play in (Monday = 1).',
      src: '06 §4.7; CLK-013',
    },
  ),
  own(
    '06',
    'calendar.cup_week_regular_weekdays',
    [
      [2, 3],
      [4, 5],
    ],
    'structure',
    {
      ...S5,
      ...F,
      desc: "The two 2-day windows of a team cup's week that regular events play in; both end by Friday.",
      src: '06 §4.7; CLK-013',
    },
  ),
  own('06', 'calendar.warmup_slots_on', [true, true, true, true], 'structure', {
    ...S5,
    ...F,
    desc: 'Whether each of the four major weeks has a warm-up event on Monday and Tuesday.',
    src: '06 §4.7; CLK-013',
  }),
  own(
    '06',
    'calendar.tour_school_days',
    { stage_1: [65, 66], stage_2: [76, 77], final: [86, 88] },
    'structure',
    {
      ...S5,
      ...F,
      desc: 'The season days of Tour School: stage 1 in week 10, stage 2 in week 11, the final in the Break Week.',
      src: '06 §4.7; CLK-017',
    },
  ),
  own('06', 'calendar.equipment_reveal_day', 85, 'days', {
    ...S5,
    ...F,
    range: [85, 85],
    desc: 'The season day the new equipment is revealed: the first day of the Break Week.',
    src: '06 §4.7; CLK-015',
  }),
  own(
    '06',
    'calendar.team_cup_days',
    [
      [47, 49],
      [89, 91],
    ],
    'structure',
    {
      ...S5,
      ...F,
      desc: "The season days of the two team cups: the professionals' cup in week 7, the amateurs' in the Break Week.",
      src: '06 §4.7; CLK-126',
    },
  ),
  own('06', 'calendar.derby_weeks', [4, 10], 'weeks', {
    ...S5,
    ...F,
    desc: 'The two weeks of the season that hold the derbies.',
    src: '06 §4.7; CLK-018',
  }),
  own(
    '06',
    'calendar.big_open_days',
    [
      [13, 14],
      [34, 35],
      [55, 56],
      [76, 77],
    ],
    'structure',
    {
      ...S5,
      ...F,
      desc: "The weekends of the club's four big opens.",
      src: '06 §4.7; CLK-018',
    },
  ),
  own('06', 'calendar.club_championship_days', [69, 70], 'days', {
    ...S5,
    ...F,
    desc: 'The weekend of the club championship: a playing week with no major.',
    src: '06 §4.7; CLK-018',
  }),
  own('06', 'calendar.presentation_day', 87, 'days', {
    ...S5,
    ...F,
    range: [85, 88],
    desc: "The season day of the club's presentation night.",
    src: '06 §4.7; CLK-018',
  }),
  own('06', 'calendar.awards_day', 88, 'days', {
    ...S5,
    ...F,
    range: [85, 88],
    desc: 'The season day of the season awards.',
    src: '06 §4.7; CLK-125',
  }),
  own('06', 'calendar.generate_ahead_seasons', 1, 'seasons', {
    ...S5,
    ...F,
    range: [1, 2],
    desc: 'How many seasons ahead the calendar is made.',
    src: '06 §4.7; CLK-030',
  }),
  own('06', 'calendar.real_major_min_overlap_days', 2, 'days', {
    ...S5,
    ...F,
    range: [1, 4],
    desc: 'The fewest days a major must share with the real event of its slot to be aligned to it.',
    src: '06 §4.7; CLK-022',
  }),
  own(
    '06',
    'calendar.real_major_fallback',
    [
      { slot_code: 'men_1', month: 'april', ends_nth_sunday: 2, region_hub: 'usa' },
      { slot_code: 'men_2', month: 'may', ends_nth_sunday: 3, region_hub: 'usa' },
      { slot_code: 'men_3', month: 'june', ends_nth_sunday: 3, region_hub: 'usa' },
      { slot_code: 'men_4', month: 'july', ends_nth_sunday: 3, region_hub: 'uki' },
      { slot_code: 'women_1', month: 'april', ends_nth_sunday: 4, region_hub: 'usa' },
      { slot_code: 'women_2', month: 'june', ends_nth_sunday: 1, region_hub: 'usa' },
      { slot_code: 'women_3', month: 'june', ends_nth_sunday: 4, region_hub: 'usa' },
      { slot_code: 'women_4', month: 'july', ends_nth_sunday: 2, region_hub: 'uki' },
      { slot_code: 'women_5', month: 'august', ends_nth_sunday: 1, region_hub: 'uki' },
    ],
    'structure',
    {
      ...S5,
      ...F,
      desc: 'Where each real major usually falls, used when no real week is entered for it. An empty list switches the fallback off.',
      src: '06 §4.7; CLK-021 (VERIFY AT BUILD: starting values, not checked against a source)',
    },
  ),
  own('06', 'calendar.club_seniors_day', 24, 'days', {
    ...S5,
    ...F,
    desc: "The season day of the club's seniors' day: a playing-week day with no major.",
    src: '06 §4.7; CLK-018',
  }),
  own('06', 'calendar.confirm_days_before_start', 14, 'days', {
    ...S5,
    ...F,
    range: [7, 28],
    desc: 'Days before a season starts that its calendar is confirmed and can no longer move without notice.',
    src: '06 §4.7; CLK-031',
  }),
  own('06', 'calendar.min_notice_days_for_moves', 14, 'days', {
    ...S5,
    ...F,
    range: [7, 28],
    desc: 'The fewest days of notice before a confirmed event may move.',
    src: '06 §4.7; CLK-032',
  }),
  own('06', 'calendar.freeze_before_transition_hours', 72, 'hours', {
    ...S5,
    ...F,
    range: [24, 168],
    desc: 'Hours before a season change-over in which no new event may be put in the calendar.',
    src: '06 §4.7; CLK-034',
  }),
  own('06', 'calendar.transition_quiet_window_min', 180, 'minutes', {
    ...S5,
    ...F,
    range: [60, 360],
    desc: 'Minutes around a season change-over in which no live event is scheduled.',
    src: '06 §4.7; CLK-034',
  }),
  own(
    '06',
    'calendar.fixed_slots',
    [
      { code: 'lunchtime', start_local: '12:00', end_local: '13:00' },
      { code: 'after_school', start_local: '16:00', end_local: '17:00' },
      { code: 'evening', start_local: '19:00', end_local: '21:00' },
    ],
    'structure',
    {
      ...S5,
      ...F,
      desc: 'The three fixed live slots of a local day, in order and not overlapping.',
      src: '06 §4.7; CLK-050',
    },
  ),

  // Stage 5: the world map and the course finder (07 §4; decision record 0306).
  own('07', 'map.pin_cluster_zoom', 8, 'level', {
    ...S5,
    client: true,
    range: [5, 12],
    desc: 'Below this zoom the world map gathers course pins into clusters.',
    src: '07 §4; CRS-019',
  }),
  own('07', 'map.weather_overlay_refresh_min', 30, 'minutes', {
    ...S5,
    client: true,
    range: [10, 120],
    desc: "How often the world map's weather tint of each area is read again.",
    src: '07 §4; CRS-020',
  }),
  own('07', 'finder.max_results', 10, 'count', {
    ...S5,
    client: true,
    range: [5, 25],
    desc: 'The most courses the course finder returns for a place.',
    src: '07 §4; CRS-103',
  }),
  own('07', 'finder.place_radius_km', 50, 'km', {
    ...S5,
    client: true,
    range: [10, 200],
    desc: 'How far from a place the course finder looks for courses.',
    src: '07 §4; CRS-103',
  }),
];

/** The batch that approves a key's version 1 (`30` SEED-150, version 2). */
export function configBatchOf(c: ConfigKeySpec): string {
  return 'stage' + String(c.stage ?? 0) + '_initial';
}

/** The registry by key. */
export const CONFIG_BY_KEY: ReadonlyMap<string, ConfigKeySpec> = new Map(
  CONFIG_DEFAULTS.map((c) => [c.key, c]),
);

/** The default of a key; throws for a key the registry does not hold. */
export function configDefault(key: string): ConfigValue {
  const c = CONFIG_BY_KEY.get(key);
  if (c === undefined) throw new Error('config: no key ' + key);
  return c.default;
}

/** Every default, by key: what `ops.game_config` version 1 holds (SEED-150). */
export function configDefaults(): Readonly<Record<string, ConfigValue>> {
  const out: Record<string, ConfigValue> = {};
  for (const c of CONFIG_DEFAULTS) out[c.key] = c.default;
  return out;
}
